import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Save, Share2, Plus, Eye, EyeOff, Trash2, Send, CheckCircle, Loader2, Tag } from 'lucide-react';
import IvisoraLogo from '../components/shared/IvisoraLogo';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import LeftToolbar from '../components/Editor/LeftToolbar';
import ContentPanel from '../components/Editor/ContentPanel';
import CanvasEditor from '../components/Editor/CanvasEditor';
import PageThumbnailPreview from '../components/Editor/PageThumbnailPreview';
import RightSidebar from '../components/Editor/RightSidebar';
import CanvasBottomBar from '../components/Editor/CanvasBottomBar';
import PagesBottomPanel from '../components/Editor/PagesBottomPanel';
import GridViewModal from '../components/Editor/GridViewModal';
import TextFloatingToolbar from '../components/Editor/TextFloatingToolbar';
import ImageFloatingToolbar from '../components/Editor/ImageFloatingToolbar';
import { resolveImageReplaceTarget, buildReplacePatch } from '../components/Editor/imageReplaceTarget';
import { canConvertToFrame, convertElementToFrame, getFrameClip } from '../components/Editor/frameClip';
import { saveCustomFrame, currentUserId } from '../components/Editor/customFrameStore';
import { frameThumbnailDataUrl } from '../components/Editor/frameThumbnail';
import CropPanel from '../components/Editor/CropPanel';
import SharedSessionBanner from '../components/Editor/SharedSessionBanner';
import ShareDesignModal from '../components/Editor/ShareDesignModal';
import UpgradeModal from '../components/shared/UpgradeModal';
import { loadBrandKitFromStorage, saveBrandKitToStorage } from '../components/Editor/BrandKitPanel';
import { useToast } from '../hooks/use-toast';
import html2canvas from 'html2canvas';
import { encodeThumbnail } from '../utils/thumbnailEncode';
import { getCategoriesForSlug } from '../constants/templateCategories';
import { getDescendants, getAncestorChain } from '../lib/groups/groupUtils';
import { migrateLegacyGroups } from '../lib/groups/migrateLegacyGroups';
import { detectSuspiciousInPages, summarizeWarnings } from '../lib/text/detectSuspiciousText';
import { collectFontFamilies } from '../lib/collectFonts';
import { preloadFonts } from '../lib/fonts';
import { shouldRedirectToUserEditor } from '../lib/editorNavigation';
import { designerSaveEndpoint } from '../lib/designerSave';
import TemplateUnavailable from '../components/Editor/TemplateUnavailable';
import { describeTemplateLoadFailure } from '../lib/templateLoadFailure';
import { loginPath } from '../lib/authRedirect';
import { clearUserSession } from '../utils/auth';
import { resolveTemplateContent } from '../lib/resolveTemplateContent';

/**
 * Normalize externally-loaded page elements through the legacy-groupId
 * migration. Pure, idempotent, never mutates input. Returns a new pages
 * array. Used at every external-data entry point (API fetch, localStorage
 * hydration, design import). NOT used for in-editor mutations — those
 * already operate on the migrated model.
 */
const migratePages = (pages) => {
  if (!Array.isArray(pages)) return pages;
  return pages.map((page) => ({
    ...page,
    elements: migrateLegacyGroups(page.elements),
  }));
};

/**
 * Strip non-persisted fields (currently: `derivedBounds` cache on group
 * elements) before sending pages to the backend or localStorage. Pure
 * function; returns a NEW array. Idempotent — safe to call on already-
 * stripped data. Bounds are recomputed lazily on first read after load.
 */
const stripDerivedFields = (pages) => {
  if (!Array.isArray(pages)) return pages;
  return pages.map((page) => ({
    ...page,
    elements: (page.elements || []).map((el) => {
      if (el.type !== 'group') return el;
      const { derivedBounds: _drop, ...rest } = el;
      return rest;
    }),
  }));
};



const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const DESIGNER_TOKEN_KEY = 'user_token';
// Returns whichever auth token is present — regular users use AUTH_TOKEN_KEY, designers use DESIGNER_TOKEN_KEY
const getEditorToken = () => localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(DESIGNER_TOKEN_KEY);

// ── Error Boundary ───────────────────────────────────────────────────────────
class EditorErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('[Editor] Runtime error caught by boundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-screen flex flex-col items-center justify-center bg-gray-50 gap-4 p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-2">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-gray-900">Editor encountered an error</h2>
          <p className="text-gray-500 text-sm max-w-sm">
            {this.state.error?.message || 'An unexpected error occurred.'}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-2 px-6 py-2.5 bg-violet-600 text-white rounded-xl text-sm font-semibold hover:bg-violet-700 transition-colors"
          >
            Reload Editor
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const EditorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: designIdFromUrl } = useParams();
  const { toast } = useToast();
  const template = location.state?.template;
  const categoryName = location.state?.categoryName;
  const customCanvasSize = location.state?.canvasSize;
  const designType = location.state?.designType || null;
  const templateTypeId = location.state?.templateTypeId || null;
  const typeSlugFromState = location.state?.typeSlug || null;
  const templateIdFromUrl = new URLSearchParams(location.search).get('template_id');
  const sessionParam = new URLSearchParams(location.search).get('session');
  const isSharedSession = sessionParam === 'shared' && !!templateIdFromUrl;

  // Shared-session attribution + upgrade state
  const [sharedTemplateName, setSharedTemplateName] = useState(null);
  const [templateShareSettings, setTemplateShareSettings] = useState(null);
  // Expanded share-info payload — surfaced into the redesigned Share
  // modal (thumbnail, description, view/use/download counters).
  const [templateShareInfo, setTemplateShareInfo] = useState(null);
  // Backend-authoritative edit permission for the resolved template.
  // Populated by an on-open fetch to /api/templates/{id}/share-info so
  // the Share modal can render toggle switches for admin/owners
  // regardless of which editor route opened it.
  const [shareEditPermission, setShareEditPermission] = useState(false);
  const [showSharedUpgrade, setShowSharedUpgrade] = useState(false);
  // Share-button modal
  const [showShareModal, setShowShareModal] = useState(false);
  // Public design-share token (only meaningful when no templateId is in
  // play and the design has been server-side enabled for public viewing).
  const [designShareToken, setDesignShareToken] = useState(null);
  // Source template id for download analytics:
  //   shared session  → templateIdFromUrl
  //   owned copy      → design.source_template_id (set when /designs/{id} loads)
  //   scratch design  → null (download flow skips analytics + watermark)
  const [sourceTemplateId, setSourceTemplateId] = useState(null);
  // Mirror of design.session_type from /designs/{id}. Forwarded back in
  // POST/PUT bodies so create/save preserve provenance.
  const [sessionType, setSessionType] = useState(null);

  // Determine initial canvas size (used when not loading a saved design)
  const getInitialCanvasSize = () => {
    if (customCanvasSize) {
      return customCanvasSize;
    }
    if (template) {
      return {
        width: template.canvas_width || template.width || 1080,
        height: template.canvas_height || template.height || 1080,
      };
    }
    return { width: 1080, height: 1080 };
  };

  const initialSize = getInitialCanvasSize();

  const [designId, setDesignId] = useState(null); // Saved design id from server (null = new/unsaved)
  // Designer Portal template id (designer_templates collection). Kept fully
  // separate from `designId` (user designs). Seeded from /designer/editor/:id
  // or an existing designer_template passed via navigation state.
  const [designerTemplateId, setDesignerTemplateId] = useState(() => {
    if (location.pathname.startsWith('/designer/') && designIdFromUrl) return designIdFromUrl;
    if (template?._source === 'designer_template' && template?.id) return template.id;
    return null;
  });
  const [designLoading, setDesignLoading] = useState(!!(designIdFromUrl || templateIdFromUrl));
  // Set when the template this editor was asked to open cannot be loaded; the
  // page then explains why instead of showing an empty, usable-looking canvas.
  const [templateLoadError, setTemplateLoadError] = useState(null);

  const [designName, setDesignName] = useState(
    categoryName
      ? `${categoryName} - ${initialSize.name || 'Design'}`
      : template?.name || designType?.name || 'Untitled Design'
  );

  const [pageName, setPageName] = useState('Page 1');
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedElements, setSelectedElements] = useState([]); // Multi-selection support
  const [editingGroupId, setEditingGroupId] = useState(null);
  const selectedElementRef = useRef(null); // Ref to track current selection for shift+click
  const [canvasSize, setCanvasSize] = useState(initialSize);
  const [canvasBackground, setCanvasBackground] = useState(() => {
    const firstPage = location.state?.pages?.[0] || template?.template_json?.pages?.[0];
    return firstPage?.background || '#ffffff';
  });
  const [backgroundConfig, setBackgroundConfig] = useState(() => {
    const firstPage = location.state?.pages?.[0] || template?.template_json?.pages?.[0];
    return firstPage?.backgroundConfig || { type: 'color', color: firstPage?.background || '#ffffff', size: 'cover', position: 'center center', opacity: 1, blur: 0 };
  });

  // Keep ref in sync with state
  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  // Left sidebar panel state
  const [activePanel, setActivePanel] = useState(null);

  // Text edit request: when a text panel button is clicked while a text element is selected,
  // set this to { id, stamp } to trigger CanvasEditor's inline text editing mode.
  const [editRequest, setEditRequest] = useState(null);
  const enterTextEdit = (id) => setEditRequest({ id, stamp: Date.now() });

  // Live filter preview: when the user hovers a filter card in the right sidebar,
  // CanvasEditor renders { id, filter } as a transient override on top of the real
  // element.filter, so the canvas image shows the effect immediately without committing.
  const [previewFilter, setPreviewFilter] = useState(null);

  // Calculate initial zoom to fit canvas in viewport
  const calculateInitialZoom = () => {
    const availableHeight = window.innerHeight - 64 - 56 - 128 - 20;
    const availableWidth = window.innerWidth - 92 - 300 - 40; // 92 = toolbar + panel when closed

    const widthRatio = availableWidth / (canvasSize?.width || initialSize.width);
    const heightRatio = availableHeight / (canvasSize?.height || initialSize.height);

    const calculatedZoom = Math.min(widthRatio, heightRatio, 2);
    return Math.max(0.1, Math.round(calculatedZoom * 100) / 100);
  };

  const [zoom, setZoom] = useState(calculateInitialZoom());
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const pagesRef = useRef(null);
  const [showNewDesignDialog, setShowNewDesignDialog] = useState(false);
  const [newCanvasWidth, setNewCanvasWidth] = useState(1080);
  const [newCanvasHeight, setNewCanvasHeight] = useState(1080);
  // Normalize AI-generator elements to use editor field names.
  // Generator stores text as `content`; editor expects `text`.
  const normalizePageElements = (pages) =>
    pages.map(p => ({
      ...p,
      elements: (p.elements || []).map(el =>
        el.type === 'text' && el.content !== undefined && el.text === undefined
          ? { ...el, text: el.content }
          : el
      ),
    }));

  const [pages, setPages] = useState(() => {
    if (location.state?.pages?.length > 0) {
      return normalizePageElements(location.state.pages.map(p => ({ ...p })));
    }
    if (template?.template_json?.pages?.length > 0) {
      return normalizePageElements(template.template_json.pages.map(p => ({ ...p })));
    }
    return [{ id: '1', name: 'Page 1', elements: [], background: '#ffffff', visible: true }];
  });
  const [currentPageId, setCurrentPageId] = useState(() => {
    if (location.state?.pages?.length > 0) {
      return location.state.pages[0].id;
    }
    if (template?.template_json?.pages?.length > 0) {
      return template.template_json.pages[0].id;
    }
    return '1';
  });
  // Page-switch reset: isolation context lives per-page (editingGroupId
  // references an element id in the current page's elements array; switching
  // to another page would leave a stale id pointing at no-such-element).
  // Reset whenever currentPageId changes. Declared AFTER currentPageId so
  // the dep array reads a live binding, not a TDZ/var-hoisted undefined.
  useEffect(() => {
    setEditingGroupId(null);
  }, [currentPageId]);
  const [showPagePanel, setShowPagePanel] = useState(true);
  const [showGridView, setShowGridView] = useState(false);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);
  const [animationPlayKey, setAnimationPlayKey] = useState(0);
  const [brandKit, setBrandKit] = useState(() => loadBrandKitFromStorage());

  useEffect(() => {
    saveBrandKitToStorage(brandKit);
  }, [brandKit]);

  // Keep pagesRef in sync so saveToHistory always captures the latest pages
  useEffect(() => { pagesRef.current = pages; }, [pages]);

  // ── Preload the design's fonts ───────────────────────────────────────────
  // Every load path (saved design, ?template_id=, shared session, navigation
  // state, localStorage draft) funnels into `pages`, so collecting fonts here
  // covers them all. Without this, a design opened cold renders its text in the
  // browser fallback face until a font dropdown happens to fetch the family —
  // the fallback's different glyph metrics shift text width, wrapping and
  // alignment, which is exactly the "Designer Editor ≠ Live Editor" mismatch.
  // Text is DOM-rendered, so the browser reflows automatically once each
  // <link> loads; loadGoogleFont is idempotent so re-running is cheap.
  // Keyed on a sorted family signature so it only fires when the SET changes,
  // not on every keystroke.
  const fontSignature = useMemo(
    () => collectFontFamilies(pages).sort().join('|'),
    [pages],
  );
  useEffect(() => {
    if (fontSignature) preloadFonts(fontSignature.split('|'));
  }, [fontSignature]);

  // ── Auto-save to localStorage (debounced 2 s) ────────────────────────────
  // Key is per-design so multiple tabs/designs don't collide.
  // designId is null for unsaved new designs → key = 'ivisora_autosave_new'.
  // After a cloud save the key changes to 'ivisora_autosave_<id>'.
  useEffect(() => {
    if (designLoading) return; // don't capture the loading/empty state
    if (isSharedSession) return; // shared template sessions never persist to localStorage
    const key = `ivisora_autosave_${designId || 'new'}`;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({
          designName, canvasSize, pages: stripDerivedFields(pages), currentPageId, designId,
          savedAt: new Date().toISOString(),
        }));
      } catch {}
    }, 2000);
    return () => clearTimeout(timer);
  }, [pages, designName, canvasSize, currentPageId, designId, designLoading, isSharedSession]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restore draft for NEW designs on mount ───────────────────────────────
  // Only fires when opening /editor without a URL design id or template.
  // Navigation-state pages, template-via-state, and category clicks skip this.
  useEffect(() => {
    if (designIdFromUrl || templateIdFromUrl) return;
    if (location.state?.pages?.length > 0 || template) return;
    if (typeSlugFromState) return; // Category click → user wants a fresh canvas, not a stale draft
    if (customCanvasSize) return; // Came from Create Template page — respect chosen canvas size
    try {
      const raw = localStorage.getItem('ivisora_autosave_new');
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!Array.isArray(draft?.pages) || draft.pages.length === 0) return;
      setPages(migratePages(draft.pages));
      if (draft.currentPageId) setCurrentPageId(draft.currentPageId);
      if (draft.designName) setDesignName(draft.designName);
      if (draft.canvasSize?.width) setCanvasSize(draft.canvasSize);
      const restoredPage = draft.pages.find(p => p.id === draft.currentPageId) || draft.pages[0];
      if (restoredPage) {
        setCanvasBackground(restoredPage.background || '#ffffff');
        setBackgroundConfig(restoredPage.backgroundConfig || {
          type: 'color', color: restoredPage.background || '#ffffff',
          size: 'cover', position: 'center center', opacity: 1, blur: 0,
        });
      }
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Template validation (state-passed template) ──────────────────────────
  // Runs once on mount. If a template was passed via navigation state but has
  // no design data, warn the user immediately rather than showing a blank canvas.
  useEffect(() => {
    if (!template) return;
    console.log("Loaded template:", template);
    const hasPages = template.template_json?.pages?.length > 0;
    if (!hasPages) {
      console.error("Invalid template data — no pages found:", template);
      toast({
        title: 'Template has no design data',
        description: 'This template was not published with canvas content. Please re-publish it from the editor.',
        variant: 'destructive',
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── History (Undo / Redo) ────────────────────────────────────────────────
  const saveToHistory = useCallback(() => {
    const snapshot = JSON.parse(JSON.stringify(pagesRef.current));
    undoStackRef.current = [...undoStackRef.current, snapshot].slice(-50);
    redoStackRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const handleUndo = useCallback(() => {
    if (!undoStackRef.current.length) return;
    const previous = undoStackRef.current.pop();
    redoStackRef.current.push(JSON.parse(JSON.stringify(pagesRef.current)));
    setPages(previous);
    setSelectedElement(null);
    setSelectedElements([]);
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
  }, []);

  const handleRedo = useCallback(() => {
    if (!redoStackRef.current.length) return;
    const next = redoStackRef.current.pop();
    undoStackRef.current.push(JSON.parse(JSON.stringify(pagesRef.current)));
    setPages(next);
    setSelectedElement(null);
    setSelectedElements([]);
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
  }, []);

  const handleBrandKitChange = (next) => {
    setBrandKit(next);
  };

  const handleApplyBrand = (kit) => {
    const headingFont = (kit.fonts || []).find(f => f.role === 'heading')?.name;
    const bodyFont    = (kit.fonts || []).find(f => f.role === 'body')?.name;
    const brandColors = (kit.colors || []).map(c => c.hex);
    if (!headingFont && !bodyFont && !brandColors.length) return;
    setPages(prev => prev.map(page => ({
      ...page,
      elements: (page.elements || []).map((el) => {
        if (el.type !== 'text') return el;
        const isHeading = (el.fontSize || 16) >= 28;
        return {
          ...el,
          ...(headingFont && isHeading  ? { fontFamily: headingFont } : {}),
          ...(bodyFont    && !isHeading ? { fontFamily: bodyFont    } : {}),
          ...(brandColors.length && isHeading ? { color: brandColors[0] } : {}),
        };
      }),
    })));
  };

  const handlePlayAnimation = (totalDurationMs) => {
    setIsAnimationPlaying(false);
    const duration = Math.max(500, Math.min(10000, totalDurationMs || 1000));
    const t1 = setTimeout(() => {
      setAnimationPlayKey((k) => k + 1);
      setIsAnimationPlaying(true);
    }, 50);
    const t2 = setTimeout(() => setIsAnimationPlaying(false), duration + 100);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  };

  // Load saved design when opening /editor/:id
  useEffect(() => {
    if (!designIdFromUrl) {
      // Don't clear loading here if a template fetch is still pending
      if (!templateIdFromUrl) setDesignLoading(false);
      return;
    }

    // /admin/templates/:id/edit — the :id is a TEMPLATE id, never a user
    // design, so it is never looked up in /api/designs. Opened from the admin
    // list, the template arrives in router state; opened by its address (or
    // from a list row without its pages), it is fetched from the admin API.
    if (/^\/admin\/templates\/[^/]+\/edit$/.test(location.pathname)) {
      if (template?.template_json?.pages?.length > 0) {
        setDesignLoading(false);
        return;
      }
      const loadAdminTemplate = async () => {
        const adminToken = localStorage.getItem('admin_token');
        if (!adminToken || !API_URL) {
          toast({ title: 'Admin sign in required', description: 'Sign in as an admin to edit templates.', variant: 'destructive' });
          navigate('/admin/login', { replace: true });
          return;
        }
        try {
          const res = await fetch(`${API_URL}/api/admin/content/templates/${designIdFromUrl}`, {
            headers: { Authorization: `Bearer ${adminToken}` },
          });
          if (res.status === 401 || res.status === 403) {
            toast({ title: 'Admin sign in required', description: 'Sign in as an admin to edit templates.', variant: 'destructive' });
            navigate('/admin/login', { replace: true });
            return;
          }
          if (!res.ok) {
            setTemplateLoadError(describeTemplateLoadFailure(res.status, null));
            return;
          }
          const t = await res.json();
          let tj = t.template_json || {};
          if (typeof tj === 'string') {
            try { tj = JSON.parse(tj); } catch { tj = {}; }
          }
          const loadedPages = (Array.isArray(tj.pages) && tj.pages.length > 0)
            ? migratePages(normalizePageElements(tj.pages.map(p => ({ ...p, elements: Array.isArray(p.elements) ? p.elements : [] }))))
            : [{ id: '1', name: 'Page 1', elements: [], background: '#ffffff', visible: true }];
          const firstPage = loadedPages[0];
          setDesignName(t.name || 'Untitled Template');
          setCanvasSize({
            width: t.canvas_width || tj.canvas_width || 1080,
            height: t.canvas_height || tj.canvas_height || 1080,
          });
          setPages(loadedPages);
          setCurrentPageId(loadedPages.some(p => p.id === tj.current_page_id) ? tj.current_page_id : firstPage.id);
          setCanvasBackground(firstPage.background || t.canvas_background || '#ffffff');
          setBackgroundConfig(firstPage.backgroundConfig || {
            type: 'color', color: firstPage.background || t.canvas_background || '#ffffff',
            size: 'cover', position: 'center center', opacity: 1, blur: 0,
          });
        } catch {
          setTemplateLoadError(describeTemplateLoadFailure(0, null));
        } finally {
          setDesignLoading(false);
        }
      };
      loadAdminTemplate();
      return;
    }

    const token = getEditorToken();
    // A signed-out visitor opening a design by its address signs in first and
    // comes straight back (designer and admin portals have their own sign-in).
    const isUserEditorRoute = location.pathname.startsWith('/editor/');
    if (!token && API_URL && isUserEditorRoute) {
      navigate(loginPath(location.pathname + location.search), { replace: true });
      return;
    }
    if (!token || !API_URL) {
      toast({ title: 'Sign in required', description: 'Sign in to open saved designs', variant: 'destructive' });
      setDesignLoading(false);
      return;
    }
    const loadDesign = async () => {
      try {
        // Creator Portal: /editor/:id?creatorMode=1 opens a creator-owned row in
        // the `templates` collection (NOT a user design), so load it from the
        // creator endpoint. Same EditorPage/CanvasEditor engine — only the data
        // source differs by role.
        if (new URLSearchParams(location.search).get('creatorMode') === '1') {
          try {
            const cRes = await fetch(`${API_URL}/api/creator/templates/${designIdFromUrl}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (cRes.ok) {
              const ct = await cRes.json();
              const tj = ct.template_json || {};
              const ccs = {
                width: ct.canvas_width || tj.canvas_width || 1080,
                height: ct.canvas_height || tj.canvas_height || 1080,
              };
              const loadedPages = (Array.isArray(tj.pages) && tj.pages.length > 0)
                ? migratePages(normalizePageElements(tj.pages.map(p => ({ ...p, elements: Array.isArray(p.elements) ? p.elements : [] }))))
                : [{ id: '1', name: 'Page 1', elements: [], background: '#ffffff', visible: true }];
              const firstPage = loadedPages[0];
              setDesignName(ct.name || 'Untitled Template');
              setCanvasSize(ccs);
              setPages(loadedPages);
              setCurrentPageId(tj.current_page_id || firstPage.id);
              setCanvasBackground(firstPage.background || ct.canvas_background || '#ffffff');
              setBackgroundConfig(firstPage.backgroundConfig || {
                type: 'color', color: firstPage.background || ct.canvas_background || '#ffffff',
                size: 'cover', position: 'center center', opacity: 1, blur: 0,
              });
              setDesignLoading(false);
              return;
            }
            // New/empty creator template (just created) → start on a blank canvas.
            setDesignLoading(false);
            return;
          } catch { setDesignLoading(false); return; }
        }
        // Designer Portal: a /designer/editor/:id route id is a designer_template
        // id (NOT a user design). Load it from designer_templates so a refresh
        // reopens the same draft. Fall back to the legacy /api/designs path on 404.
        if (location.pathname.startsWith('/designer/')) {
          try {
            const dtRes = await fetch(`${API_URL}/api/designer/templates/${designIdFromUrl}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (dtRes.ok) {
              const dt = await dtRes.json();
              const tj = dt.template_json || {};
              const dcs = {
                width: dt.canvas_width || tj.canvas_width || 1080,
                height: dt.canvas_height || tj.canvas_height || 1080,
              };
              const loadedPages = (Array.isArray(tj.pages) && tj.pages.length > 0)
                ? migratePages(normalizePageElements(tj.pages.map(p => ({ ...p, elements: Array.isArray(p.elements) ? p.elements : [] }))))
                : [{ id: '1', name: 'Page 1', elements: [], background: '#ffffff', visible: true }];
              const firstPage = loadedPages[0];
              setDesignName(dt.title || dt.name || 'Untitled Template');
              setCanvasSize(dcs);
              setPages(loadedPages);
              setCurrentPageId(tj.current_page_id || firstPage.id);
              setCanvasBackground(firstPage.background || '#ffffff');
              setBackgroundConfig(firstPage.backgroundConfig || {
                type: 'color', color: firstPage.background || '#ffffff',
                size: 'cover', position: 'center center', opacity: 1, blur: 0,
              });
              setDesignerTemplateId(dt.id);
              setDesignLoading(false);
              return;
            }
            // Not a designer template → fall through to the legacy designs load.
          } catch { /* fall through to /api/designs */ }
        }
        const res = await fetch(`${API_URL}/api/designs/${designIdFromUrl}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          if (isUserEditorRoute) {
            // The session expired or was revoked: sign in again, then return here.
            if (localStorage.getItem(AUTH_TOKEN_KEY) === token) clearUserSession();
            toast({ title: 'Please sign in again', description: 'Your session has expired.' });
            navigate(loginPath(location.pathname + location.search), { replace: true });
            return;
          }
          toast({ title: 'Sign in required', description: 'Sign in to open saved designs', variant: 'destructive' });
          setDesignLoading(false);
          return;
        }
        if (res.status === 404) {
          toast({ title: 'Design not found', description: 'This design may have been deleted', variant: 'destructive' });
          setDesignLoading(false);
          return;
        }
        if (!res.ok) {
          toast({ title: 'Error', description: 'Failed to load design', variant: 'destructive' });
          setDesignLoading(false);
          return;
        }
        const data = await res.json();
        const cs = data.canvas_size || { width: 1080, height: 1080 };
        setDesignName(data.name || 'Untitled Design');
        setCanvasSize(cs);
        if (data.source_template_id) setSourceTemplateId(data.source_template_id);
        if (data.session_type) setSessionType(data.session_type);
        // Carry the existing public-share token, if any, into local
        // state so the Share modal can show "already shared" UX without
        // an extra round-trip.
        if (data.share_token && data.is_public_share) {
          setDesignShareToken(data.share_token);
        } else {
          setDesignShareToken(null);
        }
        setPages(
          Array.isArray(data.pages) && data.pages.length > 0
            ? migratePages(normalizePageElements(data.pages.map(p => ({ ...p, elements: Array.isArray(p.elements) ? p.elements : [] }))))
            : [{ id: '1', name: 'Page 1', elements: [], background: '#ffffff', visible: true }]
        );
        const firstPageId = data.pages?.[0]?.id || '1';
        const currentId = data.current_page_id || firstPageId;
        setCurrentPageId(currentId);
        const currentPage = data.pages?.find(p => p.id === currentId) || data.pages?.[0];
        setCanvasBackground(currentPage?.background || '#ffffff');
        setBackgroundConfig(currentPage?.backgroundConfig || { type: 'color', color: currentPage?.background || '#ffffff', size: 'cover', position: 'center center', opacity: 1, blur: 0 });
        setDesignId(data.id);

        // After loading from the cloud, apply any unsaved local draft (e.g. user refreshed before saving)
        try {
          const raw = localStorage.getItem(`ivisora_autosave_${designIdFromUrl}`);
          if (raw) {
            const draft = JSON.parse(raw);
            if (Array.isArray(draft?.pages) && draft.pages.length > 0 && draft.designId === designIdFromUrl) {
              setPages(migratePages(normalizePageElements(draft.pages.map(p => ({ ...p, elements: Array.isArray(p.elements) ? p.elements : [] })))));
              if (draft.currentPageId) setCurrentPageId(draft.currentPageId);
              if (draft.designName) setDesignName(draft.designName);
              const restoredPage = draft.pages.find(p => p.id === draft.currentPageId) || draft.pages[0];
              if (restoredPage) {
                setCanvasBackground(restoredPage.background || '#ffffff');
                setBackgroundConfig(restoredPage.backgroundConfig || {
                  type: 'color', color: restoredPage.background || '#ffffff',
                  size: 'cover', position: 'center center', opacity: 1, blur: 0,
                });
              }
              toast({ title: 'Draft restored', description: 'Unsaved changes from your last session have been restored' });
            }
          }
        } catch {}
      } catch (err) {
        toast({ title: 'Error', description: 'Failed to load design', variant: 'destructive' });
      } finally {
        setDesignLoading(false);
      }
    };
    loadDesign();
  }, [designIdFromUrl]);

  // Load template from public API when ?template_id= is in the URL.
  // Shared sessions hit the auth-gated /shared-session endpoint (which
  // enforces share_settings server-side); everything else uses the
  // existing public /templates/{id} endpoint.
  useEffect(() => {
    if (!templateIdFromUrl) return;
    const loadTemplate = async () => {
      try {
        const url = isSharedSession
          ? `${API_URL}/api/templates/${templateIdFromUrl}/shared-session`
          : `${API_URL}/api/public/templates/${templateIdFromUrl}`;
        const token = getEditorToken();
        const returnHere = loginPath(location.pathname + location.search);
        // A shared session belongs to a signed-in user (the endpoint requires
        // one): a signed-out visitor signs in first and comes straight back.
        if (isSharedSession && !token && API_URL) {
          navigate(returnHere, { replace: true });
          return;
        }
        // Always send the auth token so the backend can verify marketplace entitlement
        const opts = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const res = await fetch(url, opts);
        if (!res.ok) {
          let detail = null;
          try { detail = (await res.json())?.detail ?? null; } catch { /* not JSON */ }
          const failure = describeTemplateLoadFailure(res.status, detail);
          if (failure.kind === 'signin') {
            // The session expired or was revoked: sign in again, then return here.
            if (localStorage.getItem(AUTH_TOKEN_KEY) === token) clearUserSession();
            toast({ title: 'Please sign in again', description: 'Your session has expired.' });
            navigate(returnHere, { replace: true });
          } else if (failure.kind === 'pro_only') {
            setShowSharedUpgrade(true);
          } else if (failure.kind === 'purchase') {
            toast({ title: 'Purchase required', description: 'You need to purchase this template before using it.', variant: 'destructive' });
            navigate(`/marketplace/templates/${failure.templateId || templateIdFromUrl}`);
          } else {
            // Missing, unpublished, invalid id, uses turned off, no access, or a
            // server error: say so instead of leaving an empty canvas.
            setTemplateLoadError(failure);
          }
          return;
        }
        const raw = await res.json();
        // Reshape /shared-session response to match the public template shape
        // the rest of this effect expects (data.template_json.pages).
        const data = isSharedSession ? {
          name: raw.name,
          canvas_width: raw.canvas_width,
          canvas_height: raw.canvas_height,
          template_json: { pages: raw.pages },
          _sharedSession: true,
        } : raw;
        if (isSharedSession) {
          setSharedTemplateName(raw.name || 'Template');
          if (raw.share_settings) setTemplateShareSettings(raw.share_settings);
        }
        const cs = {
          width: data.canvas_width || 1080,
          height: data.canvas_height || 1080,
        };
        // setCanvasSize is intentionally NOT called here — it is deferred to
        // the final batch alongside setPages/setCanvasBackground/setDesignLoading
        // below. Setting it pre-await caused a one-frame render where the
        // canvas had its new (taller) size but still held the initial empty
        // page (or had stale element positions), which the user perceived as
        // a "layout snap" right after the Template loaded toast.
        setDesignName(data.name || 'Untitled Design');
        // Parse template_json (may arrive as a string from older API responses)
        let templateJson = data.template_json;
        if (typeof templateJson === 'string') {
          try { templateJson = JSON.parse(templateJson); } catch { templateJson = null; }
        }

        console.log('[Template] Loaded via ?template_id=', data.name, {
          hasTemplateJson: !!templateJson,
          pagesCount: templateJson?.pages?.length ?? 0,
          legacyElementsCount: data.elements?.length ?? 0,
        });

        // ── Path A: modern format — template_json.pages ───────────────────
        if (Array.isArray(templateJson?.pages) && templateJson.pages.length > 0) {
          const processedPages = normalizePageElements(templateJson.pages.map((page) => ({
            ...page,
            elements: Array.isArray(page.elements) ? page.elements : [],
          })));

          // Test whether a background image URL is actually reachable (CSS fails silently).
          // 1.5 s timeout — fast enough to not noticeably delay loading.
          const checkImg = (url) => new Promise(resolve => {
            const img = new window.Image();
            const t = setTimeout(() => { img.src = ''; resolve(false); }, 1500);
            img.onload  = () => { clearTimeout(t); resolve(true); };
            img.onerror = () => { clearTimeout(t); resolve(false); };
            img.src = url;
          });

          // Validate background image URLs only — element images fail gracefully via native <img> error handling.
          let backgroundWasDead = false;
          const validatedPages = await Promise.all(processedPages.map(async (page) => {
            const cfg = page.backgroundConfig;
            if (cfg?.type === 'image' && cfg?.url) {
              const ok = await checkImg(cfg.url);
              if (!ok) {
                console.warn('[Template] Background image URL is dead (Pixabay or expired link):', cfg.url);
                backgroundWasDead = true;
                // Use a visible light-gray fallback so the canvas isn't blank white.
                // Elements are preserved and still render on top.
                return {
                  ...page,
                  background: '#f3f4f6',
                  backgroundConfig: { type: 'color', color: '#f3f4f6', size: 'cover', position: 'center center', opacity: 1, blur: 0 },
                };
              }
            }
            return page;
          }));

          if (backgroundWasDead) {
            toast({
              title: 'Background image unavailable',
              description: 'The background image link has expired. Showing a fallback background — your elements are preserved.',
            });
          }

          const validatedFirstPage = validatedPages[0];

          // Count ALL element types (text, shape, svg, penPath, brush — not just image).
          const afterTotalElements = validatedPages.reduce((s, p) => s + (p.elements || []).length, 0);
          // Treat #f3f4f6 (our dead-URL fallback) as "default" for thumbnail fallback eligibility
          const defaultBgColors = ['#ffffff', '#fff', 'white', '#f3f4f6'];
          const afterBgIsDefault = validatedFirstPage.backgroundConfig?.type === 'color' &&
            (!validatedFirstPage.backgroundConfig?.color || defaultBgColors.includes(validatedFirstPage.backgroundConfig.color));

          console.log('[Template] Loaded via URL:', data.name, {
            pages: validatedPages.map(p => ({ id: p.id, elements: p.elements?.length, types: (p.elements || []).map(e => e.type) })),
            afterTotalElements,
            afterBgIsDefault,
          });

          let finalPages = validatedPages;

          // If pages are truly empty, check legacy elements field before applying thumbnail fallback
          if (afterTotalElements === 0) {
            const legacyElements = Array.isArray(data.elements) && data.elements.length > 0 ? data.elements : null;
            if (legacyElements) {
              console.log('[Template] Pages empty — merging legacy elements field onto first page', legacyElements.length);
              finalPages = validatedPages.map((page, i) =>
                i === 0 ? { ...page, elements: legacyElements } : page
              );
            } else if (afterBgIsDefault && data.thumbnail) {
              // Last resort: use thumbnail as background so the user sees something
              const thumbOk = await checkImg(data.thumbnail);
              if (thumbOk) {
                console.log('[Template] Using thumbnail as fallback background for truly empty pages');
                const thumbConfig = { type: 'image', url: data.thumbnail, size: 'contain', position: 'center center', opacity: 1, blur: 0 };
                finalPages = validatedPages.map((page) => {
                  const hasBg = (page.backgroundConfig?.type === 'image' && page.backgroundConfig?.url) ||
                    page.backgroundConfig?.type === 'gradient' ||
                    (page.background && !defaultBgColors.includes(page.background));
                  if (!hasBg) {
                    return { ...page, background: '#f8f8fc', backgroundConfig: thumbConfig };
                  }
                  return page;
                });
              }
            }
          }

          const finalFirstPage = finalPages[0];
          const finalBg = finalFirstPage.background || data.canvas_background || '#ffffff';

          setCanvasSize(cs);
          setPages(migratePages(finalPages));
          setCurrentPageId(finalFirstPage.id);
          setCanvasBackground(finalBg);
          setBackgroundConfig(finalFirstPage.backgroundConfig || {
            type: 'color', color: finalBg, size: 'cover', position: 'center center', opacity: 1, blur: 0,
          });
          setSelectedElement(null);
          setSelectedElements([]);
          return;
        }

        // ── Path B: legacy flat elements array ────────────────────────────
        console.warn('[Template] No template_json.pages — falling back to legacy elements field', {
          legacyElementsCount: data.elements?.length ?? 0,
        });

        const hasLegacyElements = Array.isArray(data.elements) && data.elements.length > 0;
        const legacyBg = data.canvas_background || data.background;

        if (!hasLegacyElements && !legacyBg) {
          console.error('[Template] No usable design data:', data.id);
          toast({
            title: 'Template has no design data',
            description: 'This template was not published with canvas content.',
            variant: 'destructive',
          });
          return;
        }

        if (!hasLegacyElements) {
          console.warn('[Template] No elements found in legacy format — canvas will appear blank');
          toast({
            title: 'Template has no design data',
            description: 'This template was not published with canvas content.',
            variant: 'destructive',
          });
        }

        // Apply legacy data into a single page
        const legacyPage = {
          id: '1',
          name: 'Page 1',
          elements: hasLegacyElements ? normalizePageElements([{ elements: data.elements }])[0].elements : [],
          background: legacyBg || '#ffffff',
          visible: true,
        };
        setCanvasSize(cs);
        setPages(migratePages([legacyPage]));
        setCurrentPageId('1');
        setCanvasBackground(legacyBg || '#ffffff');
        setBackgroundConfig({
          type: 'color',
          color: legacyBg || '#ffffff',
          size: 'cover',
          position: 'center center',
          opacity: 1,
          blur: 0,
        });
        setSelectedElement(null);
        setSelectedElements([]);
      } catch (err) {
        console.error('[Template] Failed to load template:', err);
        setTemplateLoadError(describeTemplateLoadFailure(0, null));
      } finally {
        setDesignLoading(false);
      }
    };
    loadTemplate();
  }, [templateIdFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive elements from the current page - SINGLE SOURCE OF TRUTH
  const currentPage = useMemo(() =>
    pages.find(p => p.id === currentPageId) || pages[0],
    [pages, currentPageId]
  );
  const elements = useMemo(() =>
    currentPage?.elements || [],
    [currentPage]
  );

  // Advanced tool states
  const [activeTool, setActiveTool] = useState('select');

  // Brush / Eraser tool settings — lifted here so RightSidebar can read/write them
  const [brushSettings, setBrushSettings] = useState({ size: 4, color: '#000000', opacity: 1 });
  const [selectedBrushPreset, setSelectedBrushPreset] = useState('basic');
  const [eraserSettings, setEraserSettings] = useState({ size: 20, hardness: 100, opacity: 1 });

  // Mirrors CanvasEditor's drag/resize/rotate state so context UI like the
  // floating text toolbar can hide itself during transform interactions.
  const [isInteracting, setIsInteracting] = useState(false);

  // Handle active tool change
  const handleToolChange = (tool) => {
    setActiveTool(tool);
  };

  // Default-name numbering helper (pure, used by groupSelection)
  const countExistingGroups = (els) => els.filter((e) => e.type === 'group').length;

  /**
   * Create a new group containing all currently selected elements. Refuses to
   * create groups with fewer than 2 members. Inserts the new group container
   * just after (= above in z-order) the topmost selected member. Each member
   * gets parentId = newGroup.id. Children retain their relative order.
   */
  const groupSelection = useCallback(() => {
    if (!selectedElements || selectedElements.length < 2) return;
    const memberIds = new Set(selectedElements.map((el) => el.id));

    const currentPage = pages.find((p) => p.id === currentPageId);
    if (!currentPage) return;
    const els = currentPage.elements || [];

    saveToHistory(); // Cmd+G undoable

    // Find the array index of the topmost selected element (highest index).
    let topIdx = -1;
    for (let i = 0; i < els.length; i++) {
      if (memberIds.has(els[i].id)) topIdx = i;
    }
    if (topIdx < 0) return;

    const newGroupId = `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newGroup = {
      id: newGroupId,
      type: 'group',
      name: `Group ${countExistingGroups(els) + 1}`,
      parentId: els.find((e) => memberIds.has(e.id))?.parentId ?? null,
      visible: true,
      locked: false,
      derivedBounds: null,
    };

    // Build new elements array:
    //   - Members keep their array position, just get parentId updated.
    //   - New group inserted at topIdx + 1 (just above the topmost member).
    const next = els.map((el) =>
      memberIds.has(el.id) ? { ...el, parentId: newGroupId } : el
    );
    next.splice(topIdx + 1, 0, newGroup);

    setPages((prev) =>
      prev.map((p) => (p.id === currentPageId ? { ...p, elements: next } : p))
    );

    // Select the new group (single-selection convention: clear multi, set single).
    setSelectedElements([]);
    setSelectedElement(newGroup);
  }, [selectedElements, pages, currentPageId]);

  /**
   * Ungroup: dissolve the selected group. Children's parentId becomes the
   * dissolved group's parentId (so nested groups stay nested; top-level
   * groups release children to top-level). Z-order is preserved exactly —
   * children keep their array positions; only the group container is
   * removed. Mask relationships (clippedTo / maskChildIds) reference by id,
   * not by hierarchy, so they remain intact through ungroup.
   *
   * Isolation cleanup: if the user is currently editing the group being
   * ungrouped, exit isolation (the editingGroupId would otherwise point
   * to a now-deleted element).
   */
  const ungroupSelection = useCallback(() => {
    if (!selectedElement || selectedElement.type !== 'group') return;
    const currentPage = pages.find((p) => p.id === currentPageId);
    if (!currentPage) return;
    const els = currentPage.elements || [];

    const groupId = selectedElement.id;
    const group = els.find((el) => el.id === groupId);
    if (!group) return;

    saveToHistory(); // Cmd+Shift+G undoable

    const next = els
      .filter((el) => el.id !== groupId)
      .map((el) => (el.parentId === groupId ? { ...el, parentId: group.parentId ?? null } : el));

    setPages((prev) =>
      prev.map((p) => (p.id === currentPageId ? { ...p, elements: next } : p))
    );

    // After ungrouping, select the lifted children. Identify by checking
    // the ORIGINAL parentId in els (next has rewritten parentIds already).
    const liftedChildren = next.filter((el) =>
      els.find((orig) => orig.id === el.id)?.parentId === groupId
    );
    setSelectedElements(liftedChildren);
    setSelectedElement(liftedChildren[0] ?? null);

    // Stale-editingGroupId cleanup: exit isolation if the user was editing
    // the group that just dissolved.
    if (editingGroupId === groupId) {
      setEditingGroupId(null);
    }
  }, [selectedElement, pages, currentPageId, editingGroupId]);

  // Keyboard shortcuts for multi-selection and layer navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z → Undo / Ctrl+Shift+Z → Redo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) { handleRedo(); } else { handleUndo(); }
        return;
      }

      // Never hijack typing: skip element shortcuts while a text field /
      // contentEditable is focused so native copy/paste/typing still works.
      const ae = document.activeElement;
      const inEditable = !!ae && (ae.isContentEditable || ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA');

      // Ctrl/Cmd + D / C / X / V — duplicate / copy / cut / paste.
      if ((e.ctrlKey || e.metaKey) && !inEditable) {
        const k = e.key.toLowerCase();
        if (k === 'd') {
          if (selectedElements.length > 1) { e.preventDefault(); duplicateSelectedElements(); }
          else if (selectedElement) { e.preventDefault(); duplicateElement(selectedElement); }
          return;
        }
        if (k === 'c') {
          if (copySelectionToClipboard()) e.preventDefault();
          return;
        }
        if (k === 'x') {
          if (copySelectionToClipboard()) {
            e.preventDefault();
            if (selectedElements.length > 1) deleteSelectedElements();
            else if (selectedElement) deleteElement(selectedElement.id);
          }
          return;
        }
        if (k === 'v') {
          if (Array.isArray(clipboardRef.current) && clipboardRef.current.length) {
            e.preventDefault();
            pasteClipboard();
          }
          return;
        }
      }

      // Cmd+Shift+G / Ctrl+Shift+G → Ungroup selected group (must come
      // BEFORE the plain Cmd+G branch so it captures the shift variant).
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'g') {
        e.preventDefault();
        ungroupSelection();
        return;
      }

      // Cmd+G / Ctrl+G → Group selected elements
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'g' && !e.shiftKey) {
        e.preventDefault();
        groupSelection();
        return;
      }

      // Shift+A to select all layers
      if (e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        setSelectedElements([...elements]);
        if (elements.length > 0) {
          setSelectedElement(elements[elements.length - 1]); // Set last as primary
        }
        toast({
          title: 'All layers selected',
          description: `${elements.length} layers selected`
        });
        return;
      }

      // Shift+Arrow for multi-select in layers
      if (e.shiftKey && selectedElement) {
        const currentIndex = elements.findIndex(el => el.id === selectedElement.id);
        if (currentIndex === -1) return;

        if (e.key === 'ArrowUp' && currentIndex < elements.length - 1) {
          e.preventDefault();
          const nextElement = elements[currentIndex + 1];
          setSelectedElements(prev => {
            const newSelection = prev.includes(nextElement)
              ? prev
              : [...prev, nextElement];
            return newSelection;
          });
          if (!selectedElements.includes(selectedElement)) {
            setSelectedElements(prev => [...prev, selectedElement]);
          }
          setSelectedElement(nextElement);
        } else if (e.key === 'ArrowDown' && currentIndex > 0) {
          e.preventDefault();
          const prevElement = elements[currentIndex - 1];
          setSelectedElements(prev => {
            const newSelection = prev.includes(prevElement)
              ? prev
              : [...prev, prevElement];
            return newSelection;
          });
          if (!selectedElements.includes(selectedElement)) {
            setSelectedElements(prev => [...prev, selectedElement]);
          }
          setSelectedElement(prevElement);
        }
      }

      // Escape: climb one isolation level (or clear selection at root).
      // Nested-isolation flow:
      //   inside g2 (parent g1) → ESC → enter isolation for g1
      //   inside g1 (parent null) → ESC → exit isolation (editingGroupId = null)
      //   at root (editingGroupId already null) → ESC → just clear selection
      if (e.key === 'Escape') {
        if (editingGroupId) {
          const editing = elements.find((el) => el.id === editingGroupId);
          setEditingGroupId(editing?.parentId ?? null);
        }
        setSelectedElement(null);
        setSelectedElements([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [elements, selectedElement, selectedElements, activeTool, toast, handleUndo, handleRedo, groupSelection, ungroupSelection, editingGroupId]);

  // Handle element selection - supports multi-selection with Shift key
  // Marquee selection — replace (or extend with shift) the multi-selection
  // with a list of elements computed by CanvasEditor's drag-to-select. The
  // primary selected element is the first hit so single-element panels
  // (transform, properties) still have something to show.
  const handleSelectMultiple = (els) => {
    const arr = Array.isArray(els) ? els : [];
    if (arr.length === 0) {
      setSelectedElement(null);
      setSelectedElements([]);
      return;
    }
    setSelectedElements(arr);
    setSelectedElement(arr[0]);
  };

  const handleSelectElement = (element, isShiftHeld = false) => {
    if (!element) {
      setSelectedElement(null);
      setSelectedElements([]);
      return;
    }

    if (isShiftHeld) {
      // Multi-selection mode using ref to get current value
      const currentSelected = selectedElementRef.current;

      setSelectedElements(prev => {
        const isInSelection = prev.some(el => el.id === element.id);

        if (isInSelection) {
          // Toggle off - remove from selection
          return prev.filter(el => el.id !== element.id);
        } else {
          // Add to selection
          let newSelection = [...prev];

          // If this is first shift-click, include the currently selected element
          if (newSelection.length === 0 && currentSelected && currentSelected.id !== element.id) {
            newSelection.push(currentSelected);
          }

          // Add the new element
          newSelection.push(element);
          return newSelection;
        }
      });

      setSelectedElement(element);
    } else {
      // Single selection - clear multi-selection
      setSelectedElements([]);
      setSelectedElement(element);
    }
  };

  const addElement = (element) => {
    saveToHistory();
    let finalElement = { ...element };

    // For images with known dimensions, fit to canvas and center
    if (element.type === 'image' && element.width && element.height) {
      const scale = Math.min(
        canvasSize.width / element.width,
        canvasSize.height / element.height,
        1 // never upscale images smaller than the canvas
      );
      const newW = Math.round(element.width * scale);
      const newH = Math.round(element.height * scale);
      finalElement = {
        ...finalElement,
        width: newW,
        height: newH,
        x: Math.round((canvasSize.width - newW) / 2),
        y: Math.round((canvasSize.height - newH) / 2),
      };
    }

    const newElement = {
      ...finalElement,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      pageId: currentPageId
    };

    // Update ONLY the pages array - single source of truth
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, elements: [...(Array.isArray(page.elements) ? page.elements : []), newElement] }
        : page
    ));

    setSelectedElement(newElement);

    toast({
      title: 'Element added',
      description: `${element.type} has been added to canvas`
    });
  };

  // Properties that affect the rendered size of a text element.
  // NOTE: 'text' is intentionally excluded. The bounding-box width is
  // user-controlled (drag-resize / explicit width input); recomputing it on
  // every text edit would re-wrap the static render to a different width than
  // the in-edit contentEditable just displayed, causing visible layout drift
  // on blur. Height during edit is owned by contentEditable's scrollHeight
  // (auto-grow in CanvasEditor onInput/onBlur), so the parent never needs to
  // re-measure for text content changes.
  const TEXT_SIZE_PROPS = useMemo(() => new Set([
    'fontSize', 'fontFamily', 'fontWeight', 'fontStyle',
    'letterSpacing', 'lineHeight',
  ]), []);

  // Synchronously measure the natural bounding box of a text element using a hidden DOM node.
  // Uses white-space:pre so explicit \n line-breaks are respected but no word-wrapping occurs,
  // giving the natural "fit-content" size for the given text + font combination.
  const measureTextDimensions = (el) => {
    try {
      const node = document.createElement('div');
      node.setAttribute('aria-hidden', 'true');
      node.style.cssText =
        'position:fixed;visibility:hidden;pointer-events:none;top:-9999px;left:-9999px;' +
        'white-space:pre;padding:0;margin:0;box-sizing:border-box;' +
        `font-size:${el.fontSize || '16px'};` +
        `font-family:${el.fontFamily || 'sans-serif'};` +
        `font-weight:${el.fontWeight || 'normal'};` +
        `font-style:${el.fontStyle || 'normal'};` +
        `letter-spacing:${el.letterSpacing || 'normal'};` +
        `line-height:${el.lineHeight || 'normal'};`;
      node.textContent = el.text || '';
      document.body.appendChild(node);
      const rect = node.getBoundingClientRect();
      document.body.removeChild(node);
      // Add a small buffer (4px) so selection handles never clip the text
      return { width: Math.ceil(rect.width) + 4, height: Math.ceil(rect.height) + 4 };
    } catch {
      return null;
    }
  };

  const updateElement = (id, updates) => {
    // Defensive: never let an auto-size measurement throw and discard the
    // entire update. The state mutation below is the contract; measurement
    // is a best-effort enhancement.
    let sizeUpdates = {};
    try {
      // We need the current element state to merge with updates for measurement.
      // Read it from the current pages snapshot (best effort; falls back gracefully if not found).
      const currentEl = pages.flatMap(p => p.elements).find(el => el.id === id);
      if (
        currentEl?.type === 'text' &&
        // Only auto-size when a style property changes, not when the user manually drags to resize
        Object.keys(updates).some(k => TEXT_SIZE_PROPS.has(k)) &&
        !('width' in updates) &&
        !('height' in updates)
      ) {
        const merged = { ...currentEl, ...updates };
        const measured = measureTextDimensions(merged);
        if (measured) sizeUpdates = measured;
      }
    } catch {
      sizeUpdates = {};
    }

    const finalUpdates = { ...updates, ...sizeUpdates };

    // Search across ALL pages so stale-closure currentPageId never drops an update
    setPages(prev => prev.map(page => ({
      ...page,
      elements: page.elements.map(el =>
        el.id === id ? { ...el, ...finalUpdates } : el
      ),
    })));

    if (selectedElement?.id === id) {
      setSelectedElement(prev => ({ ...prev, ...finalUpdates }));
    }

    // Also update selectedElements if the element is in multi-selection
    if (selectedElements.some(el => el.id === id)) {
      setSelectedElements(prev => prev.map(el =>
        el.id === id ? { ...el, ...finalUpdates } : el
      ));
    }
  };

  // Image/frame toolbar actions are discrete; snapshot before each so every
  // flip / fit / rotation change is one undo step.
  const updateElementWithHistory = (id, updates) => {
    saveToHistory();
    updateElement(id, updates);
  };

  // Media-panel assets are external URLs. Before assigning one to a frame or
  // mask (drag-drop), store it locally so no external URL enters the canvas —
  // same contract MediaPanel uses for add-to-canvas. Returns the local URL.
  const uploadImageFromUrl = async (externalUrl) => {
    if (!API_URL) return externalUrl;
    try {
      const res = await fetch(`${API_URL}/api/upload-from-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: externalUrl }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      if (!data.url) throw new Error('Server returned no URL');
      return data.url;
    } catch (err) {
      toast({ title: 'Image import failed', description: err.message || 'Could not import image.', variant: 'destructive' });
      return null;
    }
  };

  // Replace-image flow: remember the target, open the uploads panel in pick
  // mode; UploadPanel calls onPickImage(url) instead of adding a new element.
  const replaceTargetRef = useRef(null);
  const handleRequestReplace = () => {
    const el = selectedElement;
    if (!el || (el.type !== 'image' && el.type !== 'frame')) return;
    replaceTargetRef.current = el.id;
    setActivePanel('upload');
  };

  const handlePickImage = (url) => {
    const targetId = replaceTargetRef.current;
    if (!targetId) return false;
    const all = pagesRef.current.flatMap((p) => p.elements || []);
    const el = all.find((e) => e.id === targetId);
    replaceTargetRef.current = null;
    if (!el) return false;
    // Same routing as drag-drop: frame / plain image / clipped image (mask
    // geometry + crop preserved for clipped images).
    const target = resolveImageReplaceTarget(el, all);
    if (!target) return false;
    saveToHistory();
    updateElement(target.targetId, buildReplacePatch(target.kind, url));
    setActivePanel(null);
    return true;
  };

  // Crop mode. Snapshot once on entry → the whole crop session is one undo
  // step. Cancel restores from the backup (state then equals the snapshot, so
  // the extra history entry is a harmless no-op).
  const [cropElementId, setCropElementId] = useState(null);
  const cropBackupRef = useRef(null);

  const handleStartCrop = (id) => {
    const el = pagesRef.current.flatMap((p) => p.elements || []).find((e) => e.id === id);
    if (!el) return;
    if (el.type === 'frame' && !el.image) return; // nothing to crop yet
    if (el.type !== 'frame' && el.type !== 'image') return;
    saveToHistory();
    cropBackupRef.current = JSON.parse(JSON.stringify(el));
    setCropElementId(id);
  };

  const handleCommitCrop = () => {
    cropBackupRef.current = null;
    setCropElementId(null);
  };

  const handleCancelCrop = () => {
    const backup = cropBackupRef.current;
    if (backup) {
      if (backup.type === 'frame') {
        updateElement(backup.id, { image: backup.image });
      } else {
        updateElement(backup.id, {
          cropOffsetX: backup.cropOffsetX || 0,
          cropOffsetY: backup.cropOffsetY || 0,
          cropScale: backup.cropScale || 1,
          cropRotation: backup.cropRotation || 0,
        });
      }
    }
    cropBackupRef.current = null;
    setCropElementId(null);
  };

  const deleteElement = (id) => {
    saveToHistory();
    // Mask cleanup: release any clipping mask relationship before deleting
    const target = elements.find(el => el.id === id);
    if (target) {
      // Deleting the mask shape — release ALL content children
      const childIds = target.maskChildIds ||
        (target.maskChildId ? [target.maskChildId] : []).concat(target.clipChildId ? [target.clipChildId] : []);
      if ((target.isMaskShape || target.isClipBase) && childIds.length > 0) {
        childIds.forEach(childId => updateElement(childId, { clippedTo: null, clipMask: null }));
      }
      // Deleting a content element — remove it from the mask's child array
      if (target.clippedTo) {
        const maskEl = elements.find(el => el.id === target.clippedTo);
        if (maskEl) {
          const currentIds = maskEl.maskChildIds || (maskEl.maskChildId ? [maskEl.maskChildId] : []);
          const newIds = currentIds.filter(cId => cId !== id);
          if (newIds.length === 0) {
            updateElement(target.clippedTo, { isMaskShape: false, maskChildIds: null, maskChildId: null, isClipBase: false, clipChildId: null });
          } else {
            updateElement(target.clippedTo, { maskChildIds: newIds });
          }
        }
      }
    }

    // Update ONLY the pages array - single source of truth
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, elements: page.elements.filter(el => el.id !== id) }
        : page
    ));

    // Isolation cleanup: deleting the currently-edited group (or any
    // ancestor of it) must exit isolation, otherwise editingGroupId
    // points at a now-deleted element and the user is stuck in phantom
    // isolation. Mirrors the logic in deleteSelectedElements.
    if (editingGroupId) {
      const editingChain = [
        editingGroupId,
        ...getAncestorChain(elements, editingGroupId).map((a) => a.id),
      ];
      if (editingChain.includes(id)) {
        setEditingGroupId(null);
      }
    }

    setSelectedElement(null);
    setSelectedElements(prev => prev.filter(el => el.id !== id));

    toast({
      title: 'Element deleted',
      description: 'Element has been removed from canvas'
    });
  };

  /**
   * Remove any group element with zero direct children. Repeats until stable
   * (deleting a group may empty its parent group, etc.). Returns a NEW
   * array; idempotent. Bounded at 32 iterations for safety against
   * malformed cyclic data.
   */
  const collapseEmptyGroups = (els) => {
    let working = els;
    for (let iter = 0; iter < 32; iter++) {
      const emptyGroupIds = working
        .filter((el) => el.type === 'group')
        .filter((g) => !working.some((c) => c.parentId === g.id))
        .map((g) => g.id);
      if (emptyGroupIds.length === 0) return working;
      working = working.filter((el) => !emptyGroupIds.includes(el.id));
    }
    return working;
  };

  // Delete all selected elements (multi-layer selection).
  //
  // Group cascade (Task 15): when a selected element is a group, every
  // descendant goes with it. This replaces the temporary safety guard from
  // commit 136bc17 which blocked group deletion entirely.
  //
  // Mask cleanup: extended to use the EXPANDED toDelete set (not just the
  // ids of selectedElements) so that masked descendants of a deleted group
  // are correctly stripped from their mask shape's children-list — and
  // mask shapes deleted as descendants release any surviving children's
  // clippedTo refs.
  //
  // editingGroupId cleanup: if the user is editing a group that's about
  // to be deleted (or whose ANCESTOR is being deleted), exit isolation.
  //
  // Auto-collapse: after the filter, run collapseEmptyGroups so any group
  // whose children were all deleted disappears too (prevents ghost
  // containers that would otherwise persist with zero children).
  const deleteSelectedElements = () => {
    // 1. Build the expanded toDelete set: selected ids + descendants of any
    //    selected groups. Falls back to single selection when selectedElements
    //    is empty.
    const toDelete = new Set();
    const seeds = selectedElements.length > 0
      ? selectedElements
      : (selectedElement ? [selectedElement] : []);
    for (const el of seeds) {
      if (!el) continue;
      toDelete.add(el.id);
      if (el.type === 'group') {
        for (const d of getDescendants(elements, el.id)) toDelete.add(d.id);
      }
    }
    if (toDelete.size === 0) return;
    saveToHistory();

    // 2. Mask cleanup using the expanded toDelete set.
    //    - If a deleted element is a mask shape: release surviving children's clip refs.
    //    - If a deleted element is a content child: strip it from the mask's child list.
    toDelete.forEach((id) => {
      const target = elements.find(el => el.id === id);
      if (!target) return;
      const childIds = target.maskChildIds ||
        (target.maskChildId ? [target.maskChildId] : []).concat(target.clipChildId ? [target.clipChildId] : []);
      if ((target.isMaskShape || target.isClipBase) && childIds.length > 0) {
        childIds.filter(cId => !toDelete.has(cId)).forEach(cId => {
          updateElement(cId, { clippedTo: null, clipMask: null });
        });
      }
      if (target.clippedTo && !toDelete.has(target.clippedTo)) {
        const maskEl = elements.find(el => el.id === target.clippedTo);
        if (maskEl) {
          const currentIds = maskEl.maskChildIds || (maskEl.maskChildId ? [maskEl.maskChildId] : []);
          const newIds = currentIds.filter(cId => !toDelete.has(cId));
          if (newIds.length === 0) {
            updateElement(target.clippedTo, { isMaskShape: false, maskChildIds: null, maskChildId: null, isClipBase: false, clipChildId: null });
          } else {
            updateElement(target.clippedTo, { maskChildIds: newIds });
          }
        }
      }
    });

    // 3. Page mutation: filter out deleted ids, then auto-collapse any
    //    groups whose children were all swept by the filter.
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, elements: collapseEmptyGroups((page.elements || []).filter(el => !toDelete.has(el.id))) }
        : page
    ));

    // 4. editingGroupId cleanup: if the editing group itself OR any of its
    //    ancestors are in the delete set, exit isolation. This prevents a
    //    stale editingGroupId pointing at a deleted element. The ancestor
    //    check handles the case where a user deletes a group that
    //    CONTAINS the current editing group.
    if (editingGroupId) {
      const editingChain = [
        editingGroupId,
        ...getAncestorChain(elements, editingGroupId).map((a) => a.id),
      ];
      if (editingChain.some((id) => toDelete.has(id))) {
        setEditingGroupId(null);
      }
    }

    setSelectedElement(null);
    setSelectedElements([]);
    toast({
      title: toDelete.size === 1 ? 'Element deleted' : 'Elements deleted',
      description: `${toDelete.size} element${toDelete.size === 1 ? '' : 's'} removed from canvas`
    });
  };

  const duplicateElement = (element) => {
    saveToHistory();
    const newElement = {
      ...element,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      x: element.x + 20,
      y: element.y + 20
    };

    // Update ONLY the pages array - single source of truth
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, elements: [...(Array.isArray(page.elements) ? page.elements : []), newElement] }
        : page
    ));

    setSelectedElement(newElement);
    toast({
      title: 'Element duplicated',
      description: 'A copy has been created'
    });
  };

  // Duplicate all selected elements (multi-layer selection)
  const duplicateSelectedElements = () => {
    saveToHistory();
    const toDuplicate = selectedElements.length > 0
      ? selectedElements
      : selectedElement ? [selectedElement] : [];
    if (toDuplicate.length === 0) return;
    const newElements = toDuplicate.map((el, i) => ({
      ...el,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9) + i,
      x: el.x + 20,
      y: el.y + 20
    }));
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, elements: [...(Array.isArray(page.elements) ? page.elements : []), ...newElements] }
        : page
    ));
    setSelectedElements(newElements);
    setSelectedElement(newElements[newElements.length - 1]);
    toast({
      title: newElements.length === 1 ? 'Element duplicated' : 'Elements duplicated',
      description: `${newElements.length} copy${newElements.length === 1 ? '' : 'ies'} created`
    });
  };

  const replaceImageSrc = (id, newSrc) => {
    saveToHistory();
    updateElement(id, { src: newSrc });
  };

  // Multi-selection alignment. Aligns the selected elements against the
  // bounding box of the selection itself (Figma/Canva behaviour) — not the
  // canvas. Pure x/y rewrites; widths/heights stay untouched.
  const alignSelectedElements = (mode) => {
    if (!selectedElements || selectedElements.length < 2) return;
    saveToHistory();
    const xs = selectedElements.map(el => el.x);
    const ys = selectedElements.map(el => el.y);
    const rights = selectedElements.map(el => el.x + (el.width || 0));
    const bottoms = selectedElements.map(el => el.y + (el.height || 0));
    const minX = Math.min(...xs);
    const maxR = Math.max(...rights);
    const minY = Math.min(...ys);
    const maxB = Math.max(...bottoms);
    const updates = selectedElements.map(el => {
      const w = el.width || 0;
      const h = el.height || 0;
      switch (mode) {
        case 'left':    return { id: el.id, patch: { x: minX } };
        case 'centerH': return { id: el.id, patch: { x: (minX + maxR) / 2 - w / 2 } };
        case 'right':   return { id: el.id, patch: { x: maxR - w } };
        case 'top':     return { id: el.id, patch: { y: minY } };
        case 'centerV': return { id: el.id, patch: { y: (minY + maxB) / 2 - h / 2 } };
        case 'bottom':  return { id: el.id, patch: { y: maxB - h } };
        default:        return null;
      }
    }).filter(Boolean);
    updates.forEach(u => updateElement(u.id, u.patch));
  };

  // Distribute spacing evenly between selected elements along one axis.
  // Keeps the two extreme elements fixed; redistributes the interior ones so
  // gap between consecutive items is uniform. Needs 3+ elements to do anything.
  const distributeSelectedElements = (axis) => {
    if (!selectedElements || selectedElements.length < 3) return;
    saveToHistory();
    const isH = axis === 'horizontal';
    const sized = selectedElements
      .map(el => ({
        id: el.id,
        start: isH ? el.x : el.y,
        size: isH ? (el.width || 0) : (el.height || 0),
      }))
      .sort((a, b) => a.start - b.start);
    const first = sized[0];
    const last = sized[sized.length - 1];
    const totalSize = sized.reduce((s, it) => s + it.size, 0);
    const span = (last.start + last.size) - first.start;
    const gap = (span - totalSize) / (sized.length - 1);
    let cursor = first.start;
    sized.forEach((it, i) => {
      if (i === 0 || i === sized.length - 1) {
        cursor = it.start + it.size + gap;
        return;
      }
      updateElement(it.id, isH ? { x: cursor } : { y: cursor });
      cursor = cursor + it.size + gap;
    });
  };

  const handleNewDesign = () => {
    setShowNewDesignDialog(true);
  };

  const handleCreateNewDesign = () => {
    // Reset pages to a single empty page
    setPages([
      { id: '1', name: 'Page 1', elements: [], background: '#ffffff', visible: true }
    ]);
    setCurrentPageId('1');
    setSelectedElement(null);
    setSelectedElements([]);
    setDesignName('Untitled Design');
    setPageName('Page 1');
    setCanvasBackground('#ffffff');
    setCanvasSize({ width: newCanvasWidth, height: newCanvasHeight });
    setShowNewDesignDialog(false);
    toast({
      title: 'New design created',
      description: `Canvas size: ${newCanvasWidth}x${newCanvasHeight}px`
    });
  };

  const handleCanvasSizeChange = (newSize) => {
    const w = Math.max(1, Math.min(10000, Math.round(Number(newSize.width) || canvasSize.width)));
    const h = Math.max(1, Math.min(10000, Math.round(Number(newSize.height) || canvasSize.height)));
    setCanvasSize({ width: w, height: h });
    toast({
      title: 'Canvas resized',
      description: `New size: ${w}×${h}px`
    });
  };

  const handleSetBackground = (type, value, config = {}) => {
    const newConfig = {
      type,
      size:     config.size     ?? 'cover',
      position: config.position ?? 'center center',
      opacity:  config.opacity  ?? 1,
      blur:     config.blur     ?? 0,
    };

    if (type === 'color') {
      setCanvasBackground(value);
      newConfig.color = value;
    } else if (type === 'gradient') {
      setCanvasBackground(value);
      newConfig.gradient = value;
    } else if (type === 'image') {
      // Keep canvas div background neutral — actual image rendered by background layer in CanvasEditor
      setCanvasBackground('#ffffff');
      newConfig.url = value;
    }

    setBackgroundConfig(newConfig);

    // Persist on the page object (backgroundConfig stored alongside background string)
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? {
            ...page,
            background: type === 'color' || type === 'gradient' ? value : '#ffffff',
            backgroundConfig: newConfig,
          }
        : page
    ));

    toast({
      title: 'Background updated',
      description: 'Page background has been changed'
    });
  };

  const handleMoveLayer = (elementId, direction) => {
    saveToHistory();
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;

      const pageElements = [...page.elements];
      const index = pageElements.findIndex(el => el.id === elementId);
      if (index === -1) return page;

      if (direction === 'up' && index < pageElements.length - 1) {
        [pageElements[index], pageElements[index + 1]] = [pageElements[index + 1], pageElements[index]];
      } else if (direction === 'down' && index > 0) {
        [pageElements[index], pageElements[index - 1]] = [pageElements[index - 1], pageElements[index]];
      } else if (direction === 'top' && index < pageElements.length - 1) {
        // Bring to front: move to the end of the array (highest z).
        const [moved] = pageElements.splice(index, 1);
        pageElements.push(moved);
      } else if (direction === 'bottom' && index > 0) {
        // Send to back: move to the start of the array (lowest z).
        const [moved] = pageElements.splice(index, 1);
        pageElements.unshift(moved);
      }

      return { ...page, elements: pageElements };
    }));
  };

  // Convert a shape/svg/sticker into a custom frame (Canva-style). Replaces the
  // element in place, preserving id/geometry/appearance, so undo/redo and
  // selection work like any other edit.
  const convertToFrame = (id) => {
    const el = pagesRef.current.flatMap(p => p.elements || []).find(e => e.id === id);
    if (!canConvertToFrame(el)) return;
    const frame = convertElementToFrame(el);
    if (!frame) {
      // Open vector path (Pen Tool) — can't become a frame.
      toast({ title: 'Shape is not closed', description: 'Close the path before converting it to a frame.', variant: 'destructive' });
      return;
    }
    saveToHistory();
    setPages(prev => prev.map(page => ({
      ...page,
      elements: (page.elements || []).map(e => (e.id === id ? frame : e)),
    })));
    setSelectedElement(frame);
    toast({ title: 'Converted to frame', description: 'Drag an image onto it to fill.' });
  };

  // Save the selected frame's shape to the user's My Frames library.
  const saveAsCustomFrame = (id) => {
    const el = pagesRef.current.flatMap(p => p.elements || []).find(e => e.id === id);
    if (!el || el.type !== 'frame') return;
    const clip = getFrameClip(el);
    const name = (typeof window !== 'undefined' && window.prompt)
      ? window.prompt('Frame name', el.frameType === 'custom' ? 'My Frame' : (el.shape || 'Frame'))
      : 'My Frame';
    if (name === null) return; // cancelled
    const saved = saveCustomFrame(currentUserId(), {
      name: name || 'My Frame',
      framePath: clip.path,
      frameViewBox: { w: clip.vbW, h: clip.vbH },
      frameSource: el.frameSource || (el.frameType === 'custom' ? 'svg' : 'shape'),
      thumbnail: frameThumbnailDataUrl(clip.path, clip.vbW, clip.vbH),
    });
    if (saved) toast({ title: 'Saved to My Frames', description: `"${saved.name}" is now in your library.` });
  };

  // Toggle the locked flag on an element (quick-action / More menu).
  const toggleElementLock = (id) => {
    if (!id) return;
    saveToHistory();
    const el = pagesRef.current.flatMap(p => p.elements || []).find(e => e.id === id);
    updateElement(id, { locked: !el?.locked });
  };

  // In-app clipboard for Ctrl+C/X/V. Holds deep clones of element(s).
  const clipboardRef = useRef(null);

  const copySelectionToClipboard = () => {
    const els = selectedElements.length > 0 ? selectedElements : (selectedElement ? [selectedElement] : []);
    if (els.length === 0) return false;
    clipboardRef.current = JSON.parse(JSON.stringify(els));
    return true;
  };

  const pasteClipboard = () => {
    const items = clipboardRef.current;
    if (!Array.isArray(items) || items.length === 0) return;
    saveToHistory();
    const stamp = Date.now().toString();
    const newEls = items.map((el, i) => ({
      ...el,
      id: `${stamp}${i}${Math.random().toString(36).substr(2, 6)}`,
      x: (Number(el.x) || 0) + 20,
      y: (Number(el.y) || 0) + 20,
      pageId: currentPageId,
    }));
    setPages(prev => prev.map(page =>
      page.id === currentPageId
        ? { ...page, elements: [...(Array.isArray(page.elements) ? page.elements : []), ...newEls] }
        : page
    ));
    setSelectedElements(newEls.length > 1 ? newEls : []);
    setSelectedElement(newEls[0]);
  };

  /**
   * Drag-and-drop reorder driven by LayersPanel. Moves a layer (or, if the
   * source is a group, the group plus all of its descendants — preserving
   * their relative array order) to a new position relative to a target row.
   *
   * `position`:
   *   'above' = source ends up at a HIGHER array index than target
   *             (visually above target in the panel since the panel reverses
   *              top-level order; higher index also means higher z on canvas)
   *   'below' = source ends up immediately BEFORE target
   *
   * Constraints:
   *   - Source and target must share the same parentId. Cross-parent moves
   *     (drag into / out of a group) are out of scope for this iteration —
   *     a no-op is safer than silently re-parenting and breaking masks /
   *     isolation membership.
   *   - A group cannot be dropped onto its own descendant (cycle guard).
   *   - Dropping on self is a no-op.
   */
  const handleReorderLayer = (sourceId, targetId, position) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setPages(prev => prev.map(page => {
      if (page.id !== currentPageId) return page;
      const els = page.elements || [];

      const source = els.find(e => e.id === sourceId);
      const target = els.find(e => e.id === targetId);
      if (!source || !target) return page;

      // Same-parent only — keeps masks / clips / group membership intact.
      if ((source.parentId || null) !== (target.parentId || null)) return page;

      // Cycle guard: a group can't be moved into its own subtree.
      if (source.type === 'group') {
        const descendants = getDescendants(els, sourceId);
        if (descendants.some(d => d.id === targetId)) return page;
      }

      // Collect items to move in their CURRENT array order so z-order
      // among descendants is preserved across the move.
      const moveIds = new Set([sourceId]);
      if (source.type === 'group') {
        for (const d of getDescendants(els, sourceId)) moveIds.add(d.id);
      }
      const itemsToMove = els.filter(e => moveIds.has(e.id));
      const rest = els.filter(e => !moveIds.has(e.id));

      const targetIdx = rest.findIndex(e => e.id === targetId);
      if (targetIdx === -1) return page;
      // 'above' in the panel = AFTER target in the array (since panel reverses).
      const insertIdx = position === 'above' ? targetIdx + 1 : targetIdx;

      const next = [
        ...rest.slice(0, insertIdx),
        ...itemsToMove,
        ...rest.slice(insertIdx),
      ];

      return { ...page, elements: next };
    }));
  };

  // Apply clipping mask (Figma-style):
  //   maskElementId  = the TOP element — becomes the mask shape (its outline clips the content)
  //   maskElementId     = the TOP element — becomes the mask shape (any type allowed)
  //   contentElementIds = array of BOTTOM element IDs to clip, OR null to auto-resolve
  // Called with one arg: the selected element becomes the mask, the element below is auto-selected as content.
  const handleApplyMask = (maskElementId, contentElementIds) => {
    const maskIndex = elements.findIndex(el => el.id === maskElementId);
    if (maskIndex === -1) {
      toast({ title: 'Cannot apply mask', description: 'Mask layer not found.', variant: 'destructive' });
      return;
    }

    // Normalize: accept null, string (backward compat), or string[]
    if (!contentElementIds || (Array.isArray(contentElementIds) && contentElementIds.length === 0)) {
      // Auto-resolve: use element directly below the mask
      if (maskIndex === 0) {
        toast({
          title: 'Cannot apply mask',
          description: 'No layer below to use as content. Place this layer above a content layer.',
          variant: 'destructive'
        });
        return;
      }
      contentElementIds = [elements[maskIndex - 1].id];
    } else if (typeof contentElementIds === 'string') {
      contentElementIds = [contentElementIds];
    }

    const maskEl = elements[maskIndex];

    // Validate: all content elements must exist and be below the mask
    const contentEls = contentElementIds
      .map(id => elements.find(el => el.id === id))
      .filter(Boolean);

    if (contentEls.length === 0) {
      toast({ title: 'Cannot apply mask', description: 'Content layers not found.', variant: 'destructive' });
      return;
    }

    const allBelow = contentEls.every(el => {
      const idx = elements.findIndex(e => e.id === el.id);
      return idx < maskIndex;
    });
    if (!allBelow) {
      toast({ title: 'Cannot apply mask', description: 'The mask layer must be above all content layers.', variant: 'destructive' });
      return;
    }

    // Clean up: release all old children of this mask element
    const oldChildIds = maskEl.maskChildIds ||
      (maskEl.maskChildId ? [maskEl.maskChildId] : []).concat(maskEl.clipChildId ? [maskEl.clipChildId] : []);
    oldChildIds.forEach(id => {
      if (!contentElementIds.includes(id)) {
        updateElement(id, { clippedTo: null, clipMask: null });
      }
    });

    // Clean up: release each content element from any previous mask it belonged to
    contentEls.forEach(cEl => {
      if (cEl.clippedTo && cEl.clippedTo !== maskElementId) {
        const prevMask = elements.find(el => el.id === cEl.clippedTo);
        if (prevMask) {
          const prevChildIds = prevMask.maskChildIds || (prevMask.maskChildId ? [prevMask.maskChildId] : []);
          const newPrevIds = prevChildIds.filter(id => id !== cEl.id);
          if (newPrevIds.length === 0) {
            updateElement(prevMask.id, { isMaskShape: false, maskChildIds: null, maskChildId: null, isClipBase: false, clipChildId: null });
          } else {
            updateElement(prevMask.id, { maskChildIds: newPrevIds });
          }
        }
      }
    });

    // Apply: top element = mask shape, all others = content
    updateElement(maskElementId, {
      isMaskShape: true,
      maskChildIds: contentElementIds,
      // Clear old single-child fields
      maskChildId: null,
      isClipBase: null,
      clipChildId: null,
    });
    contentElementIds.forEach(cId => {
      updateElement(cId, { clippedTo: maskElementId, clipMask: null });
    });

    const n = contentElementIds.length;
    toast({
      title: 'Mask applied',
      description: `${n} layer${n !== 1 ? 's' : ''} clipped to "${getElementTypeName(maskEl.type)}" mask`
    });
  };

  // Helper to get element type name
  const getElementTypeName = (type) => {
    switch (type) {
      case 'penPath': return 'Pen path';
      case 'shape': return 'Shape';
      case 'image': return 'Image';
      case 'text': return 'Text';
      default: return 'Element';
    }
  };

  // Release clipping mask — accepts either the mask shape ID or a content element ID.
  // When called with mask shape: releases ALL children.
  // When called with content element: removes only that content (partial release).
  const handleRemoveMask = (elementId) => {
    const element = elements.find(el => el.id === elementId);
    if (!element) return;

    if (element.isMaskShape || element.isClipBase) {
      // Called with the mask shape: release ALL children
      const childIds = element.maskChildIds ||
        (element.maskChildId ? [element.maskChildId] : []).concat(element.clipChildId ? [element.clipChildId] : []);
      childIds.forEach(childId => updateElement(childId, { clippedTo: null, clipMask: null }));
      updateElement(elementId, {
        isMaskShape: false,
        maskChildIds: null,
        maskChildId: null,
        isClipBase: false,
        clipChildId: null,
      });

    } else if (element.clippedTo || element.clipMask) {
      // Called with a content element: partial release — only this layer leaves the mask group
      const maskShapeId = element.clippedTo || element.clipMask?.clipBaseId;
      if (maskShapeId) {
        const maskEl = elements.find(el => el.id === maskShapeId);
        if (maskEl) {
          const currentIds = maskEl.maskChildIds || (maskEl.maskChildId ? [maskEl.maskChildId] : []);
          const newIds = currentIds.filter(id => id !== elementId);
          if (newIds.length === 0) {
            // Last child removed — fully release the mask shape too
            updateElement(maskShapeId, { isMaskShape: false, maskChildIds: null, maskChildId: null, isClipBase: false, clipChildId: null });
          } else {
            updateElement(maskShapeId, { maskChildIds: newIds });
          }
        }
      }
      updateElement(elementId, { clippedTo: null, clipMask: null });

    } else {
      toast({ title: 'No mask to release', variant: 'destructive' });
      return;
    }

    toast({
      title: 'Clipping mask released',
      description: 'Element is no longer clipped'
    });
  };

  const [saveLoading, setSaveLoading] = useState(false);
  const [pendingUploads, setPendingUploads] = useState(0);
  const handleUploadStart = () => setPendingUploads(n => n + 1);
  const handleUploadEnd   = () => setPendingUploads(n => Math.max(0, n - 1));

  // Designer mode: true when role is 'designer' OR navigated via /designer/* path.
  const isDesignerMode = location.pathname.startsWith('/designer/');
  // Admin-template-edit mode: /admin/templates/:id/edit — Save persists the
  // canvas back to the admin templates collection (PUT /api/admin/content/templates/:id).
  const isAdminTemplateEdit = /^\/admin\/templates\/[^/]+\/edit$/.test(location.pathname);
  const adminTemplateId = isAdminTemplateEdit
    ? location.pathname.split('/')[3]
    : null;
  // Creator mode: /editor/:id?creatorMode=1 — Save persists canvas to the creator templates collection.
  const isCreatorMode = new URLSearchParams(location.search).get('creatorMode') === '1';
  const creatorTemplateId = isCreatorMode ? designIdFromUrl : null;
  const [showDesignerSubmitModal, setShowDesignerSubmitModal] = useState(false);
  const [designerSubmitForm, setDesignerSubmitForm] = useState({
    title: '', description: '', template_type_id: '', category: '', tags: '',
  });
  const [designerTemplateTypes, setDesignerTemplateTypes] = useState([]);

  // Dynamic subcategories derived from the selected type's slug — no API call needed
  const designerCategories = getCategoriesForSlug(
    designerTemplateTypes.find(t => t.id === designerSubmitForm.template_type_id)?.slug || ''
  );

  // Fetch template types on mount so ?type=slug can be resolved immediately for TemplatePanel
  useEffect(() => {
    if (!API_URL) return;
    fetch(`${API_URL}/api/public/template-types`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setDesignerTemplateTypes(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Resolve slug → templateTypeId for TemplatePanel.
  // Sources (highest → lowest priority): location.state.templateTypeId (direct ID),
  // location.state.typeSlug (from HomePage category click), ?type= URL param.
  const typeSlugFromUrl = new URLSearchParams(location.search).get('type');
  const resolvedSlug = typeSlugFromState || typeSlugFromUrl;
  const effectiveTemplateTypeId = templateTypeId
    || (resolvedSlug ? designerTemplateTypes.find(t => t.slug === resolvedSlug)?.id : null)
    || null;

  // Auto-open templates panel ONLY after the type ID is resolved.
  // This prevents TemplatePanel from fetching unfiltered templates during the types-load delay.
  const panelAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (panelAutoOpenedRef.current) return;          // already opened — don't re-open
    if (!typeSlugFromState && !typeSlugFromUrl) return; // not a category-click entry
    if (!effectiveTemplateTypeId) return;             // types not loaded yet — wait
    setActivePanel('templates');
    panelAutoOpenedRef.current = true;
  }, [effectiveTemplateTypeId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [designerThumbnail, setDesignerThumbnail] = useState('');
  const [designerPreview, setDesignerPreview] = useState('');
  const [designerSubmitting, setDesignerSubmitting] = useState(false);
  const [autoCapturing, setAutoCapturing] = useState(false);
  const [designerPagePreviews, setDesignerPagePreviews] = useState([]); // URL per page index
  const [previewPageIdx, setPreviewPageIdx] = useState(0);              // filmstrip selection

  const openDesignerSubmitModal = async () => {
    const token = localStorage.getItem('user_token');
    if (!token) {
      navigate('/designer/login');
      return;
    }

    setDesignerSubmitForm(f => ({ ...f, title: f.title || designName }));
    setDesignerThumbnail('');
    setDesignerPreview('');
    setDesignerPagePreviews([]);
    setPreviewPageIdx(0);
    setShowDesignerSubmitModal(true);

    // Fetch template types (optional, non-blocking)
    if (API_URL) {
      fetch(`${API_URL}/api/public/template-types`)
        .then(r => r.ok ? r.json() : [])
        .then(d => setDesignerTemplateTypes(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
    // ── Clear selection so handles/outlines don't appear in the screenshot ──
    setSelectedElement(null);
    setSelectedElements([]);

    setAutoCapturing(true);

    // Wait for React to rerender (deselection) + DOM stabilisation
    await new Promise(r => setTimeout(r, 350));

    try {
      const originalPageId = currentPageId;
      const originalBg = canvasBackground;
      const originalBgConfig = backgroundConfig;
      const origPageIndex = pages.findIndex(p => p.id === originalPageId);
      // Pre-allocate results array; capture current page first for fast modal feedback
      const pageDataUrls = new Array(pages.length).fill(null);
      const captureOrder = [
        origPageIndex,
        ...Array.from({ length: pages.length }, (_, i) => i).filter(i => i !== origPageIndex),
      ];

      // Helper: is this a fully-qualified hosted URL (http/https)?
      const isHostedUrl = (url) => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

      // ── Helper: fetch an image URL and return an HTMLImageElement, or null on CORS failure ──
      const loadImageCrossOrigin = (url) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        // Timeout — external URLs (Pixabay) may hang. Kept short so a slow/dead
        // background image can't stall the Submit modal capture for seconds.
        const t = setTimeout(() => { img.src = ''; resolve(null); }, 1500);
        img.onload = () => { clearTimeout(t); resolve(img); };
        img.src = url;
      });

      // ── Helper: draw bg-size:cover onto a 2D context ──────────────────────────
      const drawCover = (ctx, img, w, h) => {
        const scaleX = w / img.naturalWidth;
        const scaleY = h / img.naturalHeight;
        const s = Math.max(scaleX, scaleY);
        const dw = img.naturalWidth * s;
        const dh = img.naturalHeight * s;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      };

      const drawContain = (ctx, img, w, h) => {
        const scaleX = w / img.naturalWidth;
        const scaleY = h / img.naturalHeight;
        const s = Math.min(scaleX, scaleY);
        const dw = img.naturalWidth * s;
        const dh = img.naturalHeight * s;
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
      };

      try {
        // Retina-quality thumbnail target: 1500 px on the longest edge.
        // - 3x the previous 500 px cap → sharper on hi-DPI displays.
        // - Drops the `min(1, …)` upper bound so a small canvas can be
        //   supersampled (up to 2x) instead of leaving us with a
        //   pixelated thumbnail at typical card sizes (≈600×750).
        // - File-size cap is enforced downstream by encodeThumbnail's
        //   WebP-first quality ladder.
        // 900px longest edge is plenty for a card/preview thumbnail and roughly
        // halves the html2canvas pixel count vs the old 1500px/2x (which made the
        // Submit modal capture take seconds). Quality is still well above display size.
        const LONGEST_TARGET = 900;
        const MAX_SUPERSAMPLE = 1.5;
        const longestEdge = Math.max(canvasSize.width, canvasSize.height);
        const captureScale = Math.min(
          MAX_SUPERSAMPLE,
          LONGEST_TARGET / longestEdge,
        );
        const targetW = Math.round(canvasSize.width  * captureScale);
        const targetH = Math.round(canvasSize.height * captureScale);

        for (const i of captureOrder) {
          const page = pages[i];
          const bg = page.background || '#ffffff';
          const bgCfg = page.backgroundConfig || {
            type: 'color', color: bg, size: 'cover', position: 'center center', opacity: 1, blur: 0,
          };
          const bgImageUrl = bgCfg.type === 'image' && bgCfg.url ? bgCfg.url : null;

          // Only switch pages when needed; current page is already active on first iteration
          if (page.id !== currentPageId) {
            setCurrentPageId(page.id);
            setCanvasBackground(bg);
            setBackgroundConfig(bgCfg);
          }
          await new Promise(r => setTimeout(r, 200));

          const el = document.getElementById(`page-canvas-${page.id}`);
          if (!el) {
            pageDataUrls[i] = bgImageUrl || null;
            continue;
          }

          // ── Step 1: Capture elements-only layer via html2canvas ─────────────────
          // We hide the background div in the CLONE (not the live DOM) so html2canvas
          // only captures design elements. The background is drawn manually in Step 2
          // so we can correctly apply blur, opacity, and cover/contain scaling.
          const origTransform = el.style.transform;
          el.style.transform = 'none';
          void el.offsetHeight;
          // Two rAF passes ensure the browser has repainted before we snapshot
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

          let elementsCanvas = null;
          try {
            elementsCanvas = await html2canvas(el, {
              width: canvasSize.width, height: canvasSize.height,
              scale: captureScale,
              useCORS: true,
              allowTaint: false,
              backgroundColor: null,
              logging: false,
              imageTimeout: 0,
              windowWidth: canvasSize.width, windowHeight: canvasSize.height,
              x: 0, y: 0, scrollX: 0, scrollY: 0,
              // Operate on the cloned document — no live DOM mutations needed
              onclone: (_clonedDoc, clonedEl) => {
                // 1. Clear element background so only elements are captured (bg drawn in Step 2)
                clonedEl.style.background = 'transparent';
                // Hide the background image child div
                const bgDivEl = clonedEl.querySelector('[style*="background-image"]');
                if (bgDivEl) bgDivEl.style.display = 'none';

                // 2. Give clip-path def SVGs explicit canvas dimensions.
                //    html2canvas may skip width="0" height="0" SVGs, losing <clipPath> defs
                //    which are referenced via clip-path:url(#mask-clip-XXX) on content elements.
                const clipDefSvgs = clonedEl.querySelectorAll('svg[width="0"][height="0"]');
                clipDefSvgs.forEach(svg => {
                  svg.setAttribute('width', canvasSize.width);
                  svg.setAttribute('height', canvasSize.height);
                  svg.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;visibility:hidden';
                });

                // 3. Also merge all <clipPath> / <mask> defs into a single root SVG so
                //    cross-element url() references are globally resolvable in the clone.
                const allClipDefs = Array.from(clonedEl.querySelectorAll('defs > clipPath, defs > mask'));
                if (allClipDefs.length > 0) {
                  const rootSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                  rootSvg.setAttribute('width', canvasSize.width);
                  rootSvg.setAttribute('height', canvasSize.height);
                  rootSvg.style.cssText = 'position:absolute;left:0;top:0;pointer-events:none;visibility:hidden';
                  const rootDefs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
                  allClipDefs.forEach(node => rootDefs.appendChild(node.cloneNode(true)));
                  rootSvg.appendChild(rootDefs);
                  clonedEl.insertBefore(rootSvg, clonedEl.firstChild);
                }
              },
            });
          } catch {
            el.style.transform = origTransform;
            console.warn(`[Thumbnail] Page ${i + 1}: html2canvas failed`);
            pageDataUrls[i] = bgImageUrl || null;
            continue;
          }
          el.style.transform = origTransform;

          // ── Step 2: Composite bg + elements on an offscreen canvas ──────────────
          const composite = document.createElement('canvas');
          composite.width  = targetW;
          composite.height = targetH;
          const ctx = composite.getContext('2d');

          // Draw background with blur + opacity matching the canvas settings
          if (bgImageUrl) {
            const bgImg = await loadImageCrossOrigin(bgImageUrl);
            if (bgImg) {
              const sizeMode   = bgCfg.size    || 'cover';
              const opacity    = bgCfg.opacity ?? 1;
              const blurAmount = bgCfg.blur    || 0;

              ctx.save();
              ctx.globalAlpha = opacity;

              if (blurAmount > 0) {
                // Expand draw area beyond canvas bounds (bleed) so blur doesn't show
                // white/transparent halos at the edges — mirrors the CSS margin trick.
                const blurPx = blurAmount * captureScale;
                const bleed  = Math.ceil(blurPx * 2);
                const expW   = targetW + bleed * 2;
                const expH   = targetH + bleed * 2;
                const s = sizeMode === 'contain'
                  ? Math.min(expW / bgImg.naturalWidth, expH / bgImg.naturalHeight)
                  : Math.max(expW / bgImg.naturalWidth, expH / bgImg.naturalHeight);
                const dw = bgImg.naturalWidth  * s;
                const dh = bgImg.naturalHeight * s;
                const dx = -bleed + (expW - dw) / 2;
                const dy = -bleed + (expH - dh) / 2;

                ctx.filter = `blur(${blurPx}px)`;
                // Clip to canvas bounds so blurred pixels don't spill outside
                ctx.beginPath();
                ctx.rect(0, 0, targetW, targetH);
                ctx.clip();
                ctx.drawImage(bgImg, dx, dy, dw, dh);
                ctx.filter = 'none';
              } else {
                if (sizeMode === 'contain') drawContain(ctx, bgImg, targetW, targetH);
                else                        drawCover(ctx, bgImg, targetW, targetH);
              }
              ctx.restore();
            } else {
              // CORS blocked — neutral gray so elements are still visible
              ctx.fillStyle = '#f3f4f6';
              ctx.fillRect(0, 0, targetW, targetH);
            }
          } else if (bgCfg.type === 'gradient' && bgCfg.gradient) {
            // Draw gradient background using Canvas 2D API — mirrors buildGradientCss in the editor
            const grad = bgCfg.gradient;
            const type = grad.type || 'linear';
            let canvasGrad;
            if (type === 'radial' || type === 'diamond') {
              canvasGrad = ctx.createRadialGradient(targetW / 2, targetH / 2, 0, targetW / 2, targetH / 2, Math.max(targetW, targetH) / 2);
            } else {
              const angle = ((grad.angle ?? 180) * Math.PI) / 180;
              const x2 = 0.5 + 0.5 * Math.sin(angle);
              const y2 = 0.5 - 0.5 * Math.cos(angle);
              canvasGrad = ctx.createLinearGradient((1 - x2) * targetW, (1 - y2) * targetH, x2 * targetW, y2 * targetH);
            }
            (grad.colors || []).forEach((color, idx) => {
              const pos = (grad.positions?.[idx] ?? (idx / Math.max(1, grad.colors.length - 1)) * 100) / 100;
              const opacity = grad.opacities?.[idx] ?? 1;
              const m = (color || '').match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
              const rgba = m ? `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${opacity})` : color;
              canvasGrad.addColorStop(Math.min(1, Math.max(0, pos)), rgba);
            });
            ctx.fillStyle = canvasGrad;
            ctx.fillRect(0, 0, targetW, targetH);
          } else {
            // Solid color (or unknown bg type) — use the actual color value, not a fallback gray
            const solidColor = bgCfg.type === 'color' && bgCfg.color ? bgCfg.color : (bg && !/^(linear|radial|url)/.test(bg) ? bg : '#ffffff');
            ctx.fillStyle = solidColor;
            ctx.fillRect(0, 0, targetW, targetH);
          }

          // Draw elements on top (transparent layer from html2canvas)
          ctx.drawImage(elementsCanvas, 0, 0, targetW, targetH);

          // Encode with WebP-first, JPEG-fallback ladder; enforce 500 KB cap.
          const encoded = encodeThumbnail(composite, { maxBytes: 500 * 1024 });
          console.log(
            `[Thumbnail] Page ${i + 1}: ${encoded.mime} q=${encoded.quality} ` +
            `${targetW}x${targetH} → ${Math.round(encoded.bytes / 1024)} KB`
          );
          pageDataUrls[i] = encoded.dataUrl;
          // Show preview immediately after capturing each page (don't wait for all pages)
          setDesignerPagePreviews([...pageDataUrls]);
        }
      } finally {
        // Always restore the original page
        setCurrentPageId(originalPageId);
        setCanvasBackground(originalBg);
        setBackgroundConfig(originalBgConfig);
      }

      // Store results immediately for preview display in the modal
      setDesignerPagePreviews(pageDataUrls);
      const mainDataUrl = pageDataUrls[origPageIndex] || pageDataUrls.find(Boolean);
      if (mainDataUrl) {
        setDesignerThumbnail(mainDataUrl);
        setDesignerPreview(mainDataUrl);
        console.log('[Thumbnail] Main thumbnail set:', mainDataUrl.startsWith('data:') ? '(data URL)' : mainDataUrl);
      }

      // Upload data URLs to hosted storage in the background.
      // Already-hosted URLs (https://...) are kept as-is — no re-upload needed.
      if (token && API_URL) {
        (async () => {
          const hostedUrls = [...pageDataUrls];
          for (let i = 0; i < pageDataUrls.length; i++) {
            const url = pageDataUrls[i];
            if (!url) continue;
            // Skip if already a hosted URL — nothing to upload
            if (isHostedUrl(url)) continue;
            try {
              const res = await fetch(url);
              const blob = await res.blob();
              const fd = new FormData();
              fd.append('file', new File([blob], `page-${i + 1}-preview.jpg`, { type: 'image/jpeg' }));
              const up = await fetch(`${API_URL}/api/designer/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
              });
              const d = await up.json();
              if (up.ok && d.url) hostedUrls[i] = d.url;
            } catch { /* keep original URL */ }
          }
          setDesignerPagePreviews(hostedUrls);
          const mainHosted = hostedUrls[origPageIndex] || hostedUrls.find(Boolean);
          if (mainHosted) {
            setDesignerThumbnail(mainHosted);
            setDesignerPreview(mainHosted);
          }
        })();
      }
    } finally {
      setAutoCapturing(false);
    }
  };

  const handleDesignerSubmit = async () => {
    if (!designerSubmitForm.title.trim()) {
      toast({ title: 'Title required', description: 'Please enter a template title', variant: 'destructive' });
      return;
    }
    const token = localStorage.getItem('user_token');
    if (!token) {
      navigate('/designer/login');
      return;
    }

    if (!API_URL) {
      toast({ title: 'Backend not configured', description: 'Set REACT_APP_BACKEND_URL in your .env file', variant: 'destructive' });
      return;
    }
    // Guard: block submission only if canvas is truly empty (no elements AND no background image)
    const totalElements = pages.reduce((sum, p) => sum + (p.elements?.length || 0), 0);
    const hasBackground = pages.some(p => {
      const cfg = p.backgroundConfig;
      return (cfg?.type === 'image' && cfg?.url) ||
        (cfg?.type === 'gradient') ||
        (p.background && p.background !== '#ffffff' && p.background !== '#fff' && p.background !== 'white');
    });
    console.log('[Designer Submit] elements:', totalElements, 'hasBackground:', hasBackground, 'pages:', pages.length);

    if (totalElements === 0 && !hasBackground) {
      toast({
        title: 'Canvas is empty',
        description: 'Add elements or set a background before submitting.',
        variant: 'destructive',
      });
      return;
    }

    // Detect if we're editing an existing rejected designer_template (edit canvas → resubmit flow)
    const existingTemplate = template?._source === 'designer_template' && template?.id ? template : null;

    // Build template_json from current canvas state
    const template_json = {
      canvas_width: canvasSize.width,
      canvas_height: canvasSize.height,
      pages,
      current_page_id: currentPageId,
    };
    // Only include hosted URLs (http/https) — data URLs can exceed MongoDB's 16 MB document limit
    const isHosted = (url) => typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'));

    // Prefer a hosted thumbnail. If it's still an inline data URL, DEFAULT to that
    // capture (the full design render, text included) and then try to upgrade it to
    // a hosted URL. We never fall back to the raw background image — that produced a
    // misleading background-only thumbnail (no text) in the admin portal when the
    // upload failed. The data URL always shows the real design.
    let finalThumbnail = isHosted(designerThumbnail) ? designerThumbnail : '';
    let finalPreview   = isHosted(designerPreview)   ? designerPreview   : '';
    if (!finalThumbnail && typeof designerThumbnail === 'string' && designerThumbnail.startsWith('data:')) {
      // Inline capture is the guaranteed fallback (survives an upload failure).
      finalThumbnail = designerThumbnail;
      finalPreview   = designerThumbnail;
      try {
        const blob = await (await fetch(designerThumbnail)).blob();
        const fd = new FormData();
        fd.append('file', new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }));
        const up = await fetch(`${API_URL}/api/designer/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (up.ok) {
          const d = await up.json();
          if (d.url) { finalThumbnail = d.url; finalPreview = d.url; }
        }
      } catch { /* keep the inline data-URL capture (has the full design + text) */ }
    }

    const payload = {
      title: designerSubmitForm.title.trim(),
      description: designerSubmitForm.description.trim(),
      template_type: designerTemplateTypes.find(t => t.id === designerSubmitForm.template_type_id)?.name || '',
      template_type_id: designerSubmitForm.template_type_id || null,
      category: designerSubmitForm.category || null,
      tags: designerSubmitForm.tags
        ? designerSubmitForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [],
      thumbnail: finalThumbnail,
      preview_url: finalPreview,
      template_json,
      canvas_width: canvasSize.width,
      canvas_height: canvasSize.height,
      page_previews: designerPagePreviews
        .map((url, i) => isHosted(url) ? { page_index: i, page_id: pages[i]?.id, preview_url: url } : null)
        .filter(Boolean),
    };
    setDesignerSubmitting(true);
    try {
      // Single-record submit: ensure ONE designer_template exists, persist the
      // latest metadata + canvas onto it, then flip it to pending. Submit never
      // creates a new record — it always promotes the same draft.
      let templateId = designerTemplateId || (existingTemplate && existingTemplate.id) || null;
      if (!templateId) {
        const draftRes = await fetch(`${API_URL}/api/designer/templates/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: payload.title,
            template_json: payload.template_json,
            canvas_width: payload.canvas_width,
            canvas_height: payload.canvas_height,
          }),
        });
        if (draftRes.status === 401) {
          localStorage.removeItem('user_token');
          localStorage.removeItem('user');
          navigate('/designer/login');
          return;
        }
        const draftData = await draftRes.json().catch(() => ({}));
        if (!draftRes.ok) {
          toast({ title: 'Submit failed', description: draftData.detail || 'Could not create template', variant: 'destructive' });
          return;
        }
        templateId = draftData.id;
        setDesignerTemplateId(templateId);
        navigate(`/designer/editor/${templateId}`, { replace: true });
      }

      // Persist metadata + latest canvas onto the same record.
      const updateBody = JSON.stringify({
        title: payload.title,
        description: payload.description,
        template_type: payload.template_type,
        template_type_id: payload.template_type_id,
        category_id: payload.category || undefined,
        tags: payload.tags,
        thumbnail: payload.thumbnail || undefined,
        preview_url: payload.preview_url || undefined,
        page_previews: payload.page_previews,
        template_json: payload.template_json,
        canvas_width: payload.canvas_width,
        canvas_height: payload.canvas_height,
      });
      const putTemplate = (tid) => fetch(`${API_URL}/api/designer/templates/${tid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: updateBody,
      });
      let updateRes = await putTemplate(templateId);
      // A legacy design opened in the Designer Editor lives in `designs`, not
      // `designer_templates`, so its id 404s here ("Template not found"). Create
      // the designer_template draft from the current canvas and retry against it,
      // so submission promotes a real record instead of dead-ending on submit.
      if (updateRes.status === 404) {
        const draftRes = await fetch(`${API_URL}/api/designer/templates/draft`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: payload.title,
            template_json: payload.template_json,
            canvas_width: payload.canvas_width,
            canvas_height: payload.canvas_height,
          }),
        });
        const draftData = await draftRes.json().catch(() => ({}));
        if (!draftRes.ok) {
          toast({ title: 'Submit failed', description: draftData.detail || 'Could not create template', variant: 'destructive' });
          return;
        }
        templateId = draftData.id;
        setDesignerTemplateId(templateId);
        navigate(`/designer/editor/${templateId}`, { replace: true });
        updateRes = await putTemplate(templateId);
      }
      if (updateRes.status === 401) {
        localStorage.removeItem('user_token');
        localStorage.removeItem('user');
        navigate('/designer/login');
        return;
      }
      if (!updateRes.ok) {
        const err = await updateRes.json().catch(() => ({}));
        toast({ title: 'Update failed', description: err.detail || 'Could not update template', variant: 'destructive' });
        return;
      }

      // Flip draft/rejected → pending on the SAME record.
      const res = await fetch(`${API_URL}/api/designer/templates/${templateId}/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) {
        localStorage.removeItem('user_token');
        localStorage.removeItem('user');
        navigate('/designer/login');
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        toast({ title: 'Submit failed', description: data.detail || 'Could not submit template', variant: 'destructive' });
        return;
      }
      toast({
        title: existingTemplate ? 'Resubmitted!' : 'Template submitted!',
        description: 'Your template is pending admin review.',
      });
      setShowDesignerSubmitModal(false);
      setDesignerSubmitForm({ title: '', description: '', template_type_id: '', category: '', tags: '' });
      setDesignerThumbnail('');
      setDesignerPreview('');
    } catch {
      toast({ title: 'Error', description: 'Could not submit template', variant: 'destructive' });
    } finally {
      setDesignerSubmitting(false);
    }
  };

  const handleAdminTemplateSave = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token || !API_URL) {
      toast({ title: 'Admin sign in required', description: 'Please sign in as admin to save this template.', variant: 'destructive' });
      return;
    }
    if (!adminTemplateId) {
      toast({ title: 'Missing template id', variant: 'destructive' });
      return;
    }
    setSaveLoading(true);
    try {
      const template_json = {
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        pages: stripDerivedFields(pages),
        current_page_id: currentPageId,
      };
      const body = {
        name: designName,
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        canvas_background: pages.find(p => p.id === currentPageId)?.background || '#ffffff',
        template_json,
      };
      console.log('[EditorPage] admin template save', {
        id: adminTemplateId,
        pages: template_json.pages.length,
        canvas: `${body.canvas_width}x${body.canvas_height}`,
      });
      const res = await fetch(`${API_URL}/api/admin/content/templates/${adminTemplateId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: 'Save failed', description: data.detail || `HTTP ${res.status}`, variant: 'destructive' });
        return;
      }
      toast({ title: 'Template saved', description: 'Canvas changes persisted to the templates collection.' });
    } catch (e) {
      toast({ title: 'Error', description: e?.message || 'Could not save template', variant: 'destructive' });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCreatorSave = async () => {
    const token = getEditorToken();
    if (!token || !API_URL) {
      toast({ title: 'Not signed in', description: 'Please sign in to save', variant: 'destructive' });
      return;
    }
    if (!creatorTemplateId) {
      toast({ title: 'Missing template ID', variant: 'destructive' });
      return;
    }
    setSaveLoading(true);
    try {
      const template_json = {
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        pages: stripDerivedFields(pages),
        current_page_id: currentPageId,
      };
      const currentBg = pages.find(p => p.id === currentPageId)?.background || '#ffffff';
      const body = {
        name: designName,
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        canvas_background: currentBg,
        template_json,
      };

      // Thumbnail generation — capture current page only, non-blocking on failure
      let thumbnailUrl = null;
      try {
        const el = document.getElementById(`page-canvas-${currentPageId}`);
        if (el) {
          const LONGEST_TARGET = 800;
          const longestEdge = Math.max(canvasSize.width, canvasSize.height);
          const captureScale = Math.min(2, LONGEST_TARGET / longestEdge);
          const targetW = Math.round(canvasSize.width * captureScale);
          const targetH = Math.round(canvasSize.height * captureScale);

          const origTransform = el.style.transform;
          el.style.transform = 'none';
          void el.offsetHeight;
          await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

          let elementsCanvas = null;
          try {
            elementsCanvas = await html2canvas(el, {
              width: canvasSize.width, height: canvasSize.height,
              scale: captureScale,
              useCORS: true, allowTaint: false,
              backgroundColor: null,
              logging: false, imageTimeout: 0,
              windowWidth: canvasSize.width, windowHeight: canvasSize.height,
              x: 0, y: 0, scrollX: 0, scrollY: 0,
              onclone: (_clonedDoc, clonedEl) => {
                clonedEl.style.background = 'transparent';
                const bgDivEl = clonedEl.querySelector('[style*="background-image"]');
                if (bgDivEl) bgDivEl.style.display = 'none';
              },
            });
          } finally {
            el.style.transform = origTransform;
          }

          if (elementsCanvas) {
            const composite = document.createElement('canvas');
            composite.width = targetW;
            composite.height = targetH;
            const ctx = composite.getContext('2d');
            // Fill solid background color
            const solidColor = /^#|^rgb/.test(currentBg) ? currentBg : '#ffffff';
            ctx.fillStyle = solidColor;
            ctx.fillRect(0, 0, targetW, targetH);
            ctx.drawImage(elementsCanvas, 0, 0, targetW, targetH);

            const encoded = encodeThumbnail(composite, { maxBytes: 500 * 1024 });
            const blob = await (await fetch(encoded.dataUrl)).blob();
            const fd = new FormData();
            fd.append('file', new File([blob], 'creator-thumbnail.jpg', { type: 'image/jpeg' }));
            const upRes = await fetch(`${API_URL}/api/designer/upload`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
            const upData = await upRes.json().catch(() => ({}));
            if (upRes.ok && upData.url) thumbnailUrl = upData.url;
          }
        }
      } catch (thumbErr) {
        console.warn('[CreatorSave] Thumbnail generation failed (non-fatal):', thumbErr);
      }

      if (thumbnailUrl) body.thumbnail = thumbnailUrl;

      const res = await fetch(`${API_URL}/api/creator/templates/${creatorTemplateId}/canvas`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast({ title: 'Save failed', description: data.detail || `HTTP ${res.status}`, variant: 'destructive' });
        return;
      }
      toast({ title: 'Template saved', description: 'Your template has been saved to Creator Hub.' });
    } catch (e) {
      toast({ title: 'Error', description: e?.message || 'Could not save template', variant: 'destructive' });
    } finally {
      setSaveLoading(false);
    }
  };

  // Designer Portal save — keeps work entirely inside designer_templates and
  // NEVER touches the user `designs` collection. First save creates a draft
  // (POST /draft) and adopts its id in the URL so a refresh reopens the same
  // template; every later save PUTs the same record.
  const handleDesignerSave = async () => {
    const token = getEditorToken();
    if (!token || !API_URL) {
      toast({ title: 'Not signed in', description: 'Please sign in to save', variant: 'destructive' });
      return;
    }
    setSaveLoading(true);
    try {
      const template_json = {
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        pages: stripDerivedFields(pages),
        current_page_id: currentPageId,
      };
      const body = {
        title: designName,
        template_json,
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
      };
      // First save → POST /draft; subsequent saves → PUT /{id}. Never /api/designs.
      const { method, path } = designerSaveEndpoint({ designerTemplateId });
      const res = await fetch(`${API_URL}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        localStorage.removeItem('user_token');
        localStorage.removeItem('user');
        navigate('/designer/login');
        return;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast({ title: 'Save failed', description: data.detail || 'Could not save template', variant: 'destructive' });
        return;
      }
      if (!designerTemplateId && data.id) {
        setDesignerTemplateId(data.id);
        navigate(`/designer/editor/${data.id}`, { replace: true });
      }
      toast({ title: 'Draft saved', description: 'Changes saved. Use Submit for Approval when ready.' });
    } catch {
      toast({ title: 'Error', description: 'Could not save template', variant: 'destructive' });
    } finally {
      setSaveLoading(false);
    }
  };

  // Capture the current page to a compact data-URL thumbnail for the
  // My Designs / Saved cards. Non-blocking: returns null on any failure
  // so a save never fails because a preview couldn't render.
  const captureDesignThumbnail = async () => {
    try {
      const el = document.getElementById(`page-canvas-${currentPageId}`);
      if (!el) return null;
      const LONGEST_TARGET = 400;
      const longestEdge = Math.max(canvasSize.width, canvasSize.height) || 1;
      const captureScale = Math.min(1.5, LONGEST_TARGET / longestEdge);
      const targetW = Math.round(canvasSize.width * captureScale);
      const targetH = Math.round(canvasSize.height * captureScale);
      const currentBg = pages.find(p => p.id === currentPageId)?.background || '#ffffff';

      // Ensure every font used by the canvas is loaded BEFORE rasterizing, so
      // the thumbnail matches the editor (html2canvas bakes whatever face is
      // resolved at capture time — a fallback face shifts text metrics).
      try {
        preloadFonts(collectFontFamilies(pages));
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
      } catch { /* best-effort — capture proceeds regardless */ }

      const origTransform = el.style.transform;
      el.style.transform = 'none';
      void el.offsetHeight;
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      let elementsCanvas = null;
      try {
        elementsCanvas = await html2canvas(el, {
          width: canvasSize.width, height: canvasSize.height,
          scale: captureScale, useCORS: true, allowTaint: false,
          backgroundColor: null, logging: false, imageTimeout: 0,
          windowWidth: canvasSize.width, windowHeight: canvasSize.height,
          x: 0, y: 0, scrollX: 0, scrollY: 0,
          onclone: (_doc, clonedEl) => {
            clonedEl.style.background = 'transparent';
            const bgDivEl = clonedEl.querySelector('[style*="background-image"]');
            if (bgDivEl) bgDivEl.style.display = 'none';
          },
        });
      } finally {
        el.style.transform = origTransform;
      }
      if (!elementsCanvas) return null;
      const composite = document.createElement('canvas');
      composite.width = targetW;
      composite.height = targetH;
      const ctx = composite.getContext('2d');
      const solidColor = /^#|^rgb/.test(currentBg) ? currentBg : '#ffffff';
      ctx.fillStyle = solidColor;
      ctx.fillRect(0, 0, targetW, targetH);
      ctx.drawImage(elementsCanvas, 0, 0, targetW, targetH);
      const encoded = encodeThumbnail(composite, { maxBytes: 200 * 1024 });
      return encoded?.dataUrl || null;
    } catch {
      return null;
    }
  };

  const handleSave = async () => {
    if (isSharedSession) {
      // Shared session: no saves of any kind. Surface the Upgrade funnel.
      setShowSharedUpgrade(true);
      return;
    }
    const token = getEditorToken();
    if (!token || !API_URL) {
      // Fallback: save to localStorage when not signed in
      const design = {
        id: Date.now().toString(),
        name: designName,
        pages: stripDerivedFields(pages),
        canvasSize,
        updatedAt: new Date().toISOString()
      };
      const savedDesigns = JSON.parse(localStorage.getItem('designs') || '[]');
      savedDesigns.push(design);
      localStorage.setItem('designs', JSON.stringify(savedDesigns));
      toast({ title: 'Saved locally', description: 'Sign in to save to the cloud' });
      return;
    }
    setSaveLoading(true);
    try {
      // Refresh the My Designs / Saved card preview on every explicit save.
      // null is fine — the backend's update path ignores null thumbnails,
      // so a failed capture never wipes an existing preview.
      const thumbnail = await captureDesignThumbnail();
      const body = {
        name: designName,
        canvas_size: canvasSize,
        pages: stripDerivedFields(pages),
        current_page_id: currentPageId,
        thumbnail,
        // Preserve / forward template provenance across save. When the
        // editor knows its origin (loaded design with provenance, or a
        // template URL), the backend persists these so the Share modal
        // resolves template mode on next load.
        source_template_id: sourceTemplateId || templateIdFromUrl || null,
        session_type:
          sessionType || ((sourceTemplateId || templateIdFromUrl) ? 'user_copy' : null),
      };
      const url = designId ? `${API_URL}/api/designs/${designId}` : `${API_URL}/api/designs`;
      const method = designId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        toast({ title: 'Save failed', description: data.detail || 'Could not save design', variant: 'destructive' });
        return;
      }
      if (!designId) {
        setDesignId(data.id);
        // Adopt the new id in the URL for the plain user editor only. Portal
        // contexts (Designer/creator/admin) must stay on their own route —
        // bouncing a designer into /editor/:id drops the Submit-for-Approval
        // workflow. See shouldRedirectToUserEditor.
        if (shouldRedirectToUserEditor({ isDesignerMode, isCreatorMode, isAdminTemplateEdit })) {
          navigate(`/editor/${data.id}`, { replace: true });
        }
      }
      toast({ title: 'Saved to cloud', description: 'Your design has been saved' });

      // ── Advisory: surface suspicious-text warnings without blocking ──
      // The server returns a summary in `data.text_warnings`; we ALSO
      // recompute client-side as a belt-and-suspenders (catches offline
      // saves and the localStorage-only fallback path elsewhere). The
      // warning is non-blocking — saving still succeeded, the user
      // decides whether to act.
      try {
        const local = detectSuspiciousInPages(pages);
        const summary = data?.text_warnings?.summary || summarizeWarnings(local);
        if (summary && summary.count > 0) {
          const sample = (summary.samples?.[0] && `e.g. "${summary.samples[0].original}" → "${summary.samples[0].suggestion}"`) || '';
          toast({
            title: `${summary.count} possible text issue${summary.count === 1 ? '' : 's'} detected`,
            description: `Looks like text spacing may be collapsed. ${sample} Review under Layers.`,
            variant: 'default',
            duration: 8000,
          });
        }
      } catch { /* advisory only — never fail the save flow */ }
      // Clear local draft — the cloud copy is now the source of truth
      try {
        localStorage.removeItem('ivisora_autosave_new');
        if (designId) localStorage.removeItem(`ivisora_autosave_${designId}`);
      } catch {}
    } catch (err) {
      toast({ title: 'Error', description: 'Could not save design', variant: 'destructive' });
    } finally {
      setSaveLoading(false);
    }
  };

  // ── Debounced cloud autosave (4 s) ───────────────────────────────────────
  // Persists work-in-progress to the SAME /api/designs record that manual
  // Save and My Designs read, so nothing is lost between explicit saves and
  // the design stays the single source of truth. Only runs for signed-in
  // users editing a normal design — creator, admin-template, designer, and
  // shared-template sessions save to other collections and must not leak a
  // stray design row. Thumbnail capture is skipped here (expensive); manual
  // Save refreshes the preview.
  const autosaveInFlightRef = useRef(false);
  const lastAutosaveSnapshotRef = useRef('');

  useEffect(() => {
    if (designLoading) return;
    if (isSharedSession || isCreatorMode || isAdminTemplateEdit || isDesignerMode) return;
    const token = getEditorToken();
    if (!token || !API_URL) return; // only autosave to cloud when signed in
    // Don't litter My Designs with a blank, never-saved canvas.
    const hasContent = pages.some(p => (p.elements || []).length > 0);
    if (!designId && !hasContent) return;

    const snapshot = JSON.stringify({
      designName, canvasSize, currentPageId, pages: stripDerivedFields(pages),
    });
    if (snapshot === lastAutosaveSnapshotRef.current) return; // nothing changed

    const timer = setTimeout(async () => {
      if (autosaveInFlightRef.current) return;
      autosaveInFlightRef.current = true;
      try {
        // No thumbnail capture here: html2canvas on the live scaled canvas
        // flickers and is CPU-heavy to run every few seconds, and it throws
        // on cross-origin images. The My Designs / Dashboard / Saved cards
        // render a real preview straight from `pages` (DesignPreview), so an
        // autosaved design shows correctly even without a stored thumbnail.
        //
        // Fix A1 — invalidate the stored thumbnail on autosave. Autosave only
        // fires when `pages` changed (snapshot guard above), so any previously
        // captured raster is now out of date. Sending an empty string clears it
        // (the backend update path only skips `null`, so '' is persisted), which
        // forces the canonical live render everywhere instead of a stale raster.
        // We deliberately do NOT regenerate via html2canvas here (no "Fix A2").
        const body = {
          name: designName,
          canvas_size: canvasSize,
          pages: stripDerivedFields(pages),
          current_page_id: currentPageId,
          thumbnail: '',
          source_template_id: sourceTemplateId || templateIdFromUrl || null,
          session_type:
            sessionType || ((sourceTemplateId || templateIdFromUrl) ? 'user_copy' : null),
        };
        const url = designId ? `${API_URL}/api/designs/${designId}` : `${API_URL}/api/designs`;
        const res = await fetch(url, {
          method: designId ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data = await res.json().catch(() => null);
          if (!designId && data?.id) {
            // First autosave of a new design: adopt the id so the next
            // autosave PUTs instead of creating another row.
            setDesignId(data.id);
            navigate(`/editor/${data.id}`, { replace: true });
          }
          lastAutosaveSnapshotRef.current = snapshot;
        }
      } catch { /* autosave is best-effort — manual Save still available */ }
      finally { autosaveInFlightRef.current = false; }
    }, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages, designName, canvasSize, currentPageId, designId, designLoading,
      isSharedSession, isCreatorMode, isAdminTemplateEdit, isDesignerMode]);

  const handleAddPage = () => {
    const currentIndex = pages.findIndex(p => p.id === currentPageId);
    const newPage = {
      id: Date.now().toString(),
      name: `Page ${pages.length + 1}`,
      elements: [],
      background: '#ffffff',
      visible: true,
    };
    const newPages = [...pages];
    newPages.splice(currentIndex + 1, 0, newPage);
    setPages(newPages);
    setCurrentPageId(newPage.id);
    setCanvasBackground('#ffffff');
    setBackgroundConfig({ type: 'color', color: '#ffffff', size: 'cover', position: 'center center', opacity: 1, blur: 0 });
    setSelectedElement(null);
    setSelectedElements([]);
    toast({ title: 'Page added', description: `${newPage.name} created` });
  };

  const handleDuplicatePage = () => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (!currentPage) return;

    const duplicatedPage = {
      id: Date.now().toString(),
      name: `${currentPage.name} Copy`,
      elements: currentPage.elements.map(el => ({
        ...el,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
      })),
      background: currentPage.background,
      backgroundConfig: currentPage.backgroundConfig,
      visible: true
    };

    const currentIndex = pages.findIndex(p => p.id === currentPageId);
    const newPages = [...pages];
    newPages.splice(currentIndex + 1, 0, duplicatedPage);
    setPages(newPages);

    toast({
      title: 'Page duplicated',
      description: `Created a copy with all ${duplicatedPage.elements.length} elements`
    });
  };

  const handleDeletePage = (pageId) => {
    if (pages.length === 1) {
      toast({
        title: 'Cannot delete',
        description: 'You must have at least one page',
        variant: 'destructive'
      });
      return;
    }

    const pageIndex = pages.findIndex(p => p.id === pageId);
    const newPages = pages.filter(p => p.id !== pageId);
    setPages(newPages);

    if (pageId === currentPageId) {
      const newCurrentPage = newPages[Math.max(0, pageIndex - 1)];
      setCurrentPageId(newCurrentPage.id);
      setCanvasBackground(newCurrentPage.background || '#ffffff');
      setBackgroundConfig(newCurrentPage.backgroundConfig || { type: 'color', color: newCurrentPage.background || '#ffffff', size: 'cover', position: 'center center', opacity: 1, blur: 0 });
      setSelectedElement(null);
      setSelectedElements([]);
    }

    toast({
      title: 'Page deleted',
      description: 'Page has been removed'
    });
  };

  const handleTogglePageVisibility = (pageId) => {
    setPages(prev => prev.map(page =>
      page.id === pageId
        ? { ...page, visible: !page.visible }
        : page
    ));

    const page = pages.find(p => p.id === pageId);
    toast({
      title: page?.visible ? 'Page hidden' : 'Page shown',
      description: page?.visible ? 'Page is now hidden' : 'Page is now visible'
    });
  };

  const handleSwitchPage = (pageId) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return;

    // No need to save current page - pages array IS the source of truth
    setCurrentPageId(pageId);
    setCanvasBackground(page.background || '#ffffff');
    setBackgroundConfig(page.backgroundConfig || { type: 'color', color: page.background || '#ffffff', size: 'cover', position: 'center center', opacity: 1, blur: 0 });
    setSelectedElement(null);
    setSelectedElements([]);
  };

  const handleRenamePage = (pageId, newName) => {
    if (!newName.trim()) return;
    setPages(prev => prev.map(p => p.id === pageId ? { ...p, name: newName.trim() } : p));
  };

  const handleLockPage = (pageId, locked) => {
    setPages(prev => prev.map(p => p.id === pageId ? { ...p, locked } : p));
    toast({ title: locked ? 'Page locked' : 'Page unlocked' });
  };

  const handleHidePage = (pageId, hidden) => {
    setPages(prev => prev.map(p => p.id === pageId ? { ...p, hidden } : p));
    toast({ title: hidden ? 'Page hidden' : 'Page visible' });
  };

  const handleReorderPages = (newPagesArray) => {
    setPages(newPagesArray);
  };

  const handleAddPageAfter = (pageId) => {
    const pageIndex = pages.findIndex(p => p.id === pageId);
    const newPage = {
      id: Date.now().toString(),
      name: `Page ${pages.length + 1}`,
      elements: [],
      background: '#ffffff',
      visible: true,
    };
    const newPages = [...pages];
    newPages.splice(pageIndex + 1, 0, newPage);
    setPages(newPages);
    setCurrentPageId(newPage.id);
    setCanvasBackground('#ffffff');
    setBackgroundConfig({ type: 'color', color: '#ffffff', size: 'cover', position: 'center center', opacity: 1, blur: 0 });
    setSelectedElement(null);
    setSelectedElements([]);
    toast({ title: 'Page added', description: `New page added after "${pages[pageIndex]?.name}"` });
  };

  const handleDuplicatePageById = (pageId) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return;
    const pageIndex = pages.findIndex(p => p.id === pageId);
    const duplicated = {
      id: Date.now().toString(),
      name: `${page.name} Copy`,
      elements: page.elements.map(el => ({
        ...el,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      })),
      background: page.background,
      backgroundConfig: page.backgroundConfig,
      visible: true,
    };
    const newPages = [...pages];
    newPages.splice(pageIndex + 1, 0, duplicated);
    setPages(newPages);
    toast({ title: 'Page duplicated', description: `Copy of "${page.name}" created` });
  };

  const handleFitToScreen = () => {
    const availableH = window.innerHeight - 64 - 40 - 20;
    const availableW = window.innerWidth - 80 - (activePanel ? 288 : 0) - 20;
    const fit = Math.min(availableW / canvasSize.width, availableH / canvasSize.height, 2);
    setZoom(Math.max(0.1, Math.round(fit * 100) / 100));
  };

  const handleSelectTemplate = async (chosen) => {
    if (!chosen) {
      console.error('[Template] handleSelectTemplate called with null/undefined');
      return;
    }
    // Lists withhold the pages of paid/premium templates this user is not
    // entitled to; fetch them through the entitlement-checked endpoint.
    const resolved = await resolveTemplateContent(chosen, { apiUrl: API_URL, token: getEditorToken() });
    if (resolved.denied === 'purchase') {
      toast({ title: 'Purchase required', description: 'You need to purchase this template before using it.', variant: 'destructive' });
      navigate(`/marketplace/templates/${resolved.templateId}`);
      return;
    }
    if (resolved.denied === 'pro') {
      setShowSharedUpgrade(true);
      return;
    }
    if (resolved.denied === 'unavailable') {
      toast({ title: 'Template unavailable', description: 'This template could not be loaded. Please try another one.', variant: 'destructive' });
      return;
    }
    const template = resolved.template;

    // Parse template_json if it came back as a string
    let templateJson = template.template_json;
    if (typeof templateJson === 'string') {
      try { templateJson = JSON.parse(templateJson); } catch { templateJson = null; }
    }

    console.log('[Template] Loading template:', template.name, {
      hasTemplateJson: !!templateJson,
      pagesCount: templateJson?.pages?.length ?? 0,
      canvasSize: { width: template.canvas_width, height: template.canvas_height },
    });

    // Shared helper: test whether a background image URL is reachable (CSS fails silently).
    // 1.5 s timeout — fast enough to not noticeably delay loading.
    const checkImg = (url) => new Promise(resolve => {
      const img = new window.Image();
      const t = setTimeout(() => { img.src = ''; resolve(false); }, 1500);
      img.onload  = () => { clearTimeout(t); resolve(true); };
      img.onerror = () => { clearTimeout(t); resolve(false); };
      img.src = url;
    });

    // ── Path A: template_json with pages (modern format) ──────────────────
    if (Array.isArray(templateJson?.pages) && templateJson.pages.length > 0) {
      const processedPages = templateJson.pages.map(p => ({ ...p, elements: Array.isArray(p.elements) ? p.elements : [] }));

      // Validate background image URLs only — element images fail gracefully via native <img> error handling.
      let backgroundWasDead = false;
      const validatedPages = await Promise.all(processedPages.map(async (page) => {
        const cfg = page.backgroundConfig;
        if (cfg?.type === 'image' && cfg?.url) {
          const ok = await checkImg(cfg.url);
          if (!ok) {
            console.warn('[Template] Background image URL is dead (Pixabay or expired link):', cfg.url);
            backgroundWasDead = true;
            return {
              ...page,
              background: '#f3f4f6',
              backgroundConfig: { type: 'color', color: '#f3f4f6', size: 'cover', position: 'center center', opacity: 1, blur: 0 },
            };
          }
        }
        return page;
      }));

      if (backgroundWasDead) {
        toast({
          title: 'Background image unavailable',
          description: 'The background image link has expired. Showing a fallback background — your elements are preserved.',
        });
      }

      // Count ALL element types (text, shape, svg, image, etc.)
      const totalElements = validatedPages.reduce((s, p) => s + (p.elements || []).length, 0);
      const firstPage = validatedPages[0];
      const firstBgIsDefault = firstPage.backgroundConfig?.type === 'color' &&
        (!firstPage.backgroundConfig?.color || ['#ffffff', '#fff', 'white', '#f3f4f6'].includes(firstPage.backgroundConfig.color));

      console.log('[Template] Loaded:', template.name, {
        pages: validatedPages.map(p => ({ id: p.id, elements: p.elements?.length, types: (p.elements || []).map(e => e.type) })),
        totalElements,
        firstBgIsDefault,
        backgroundWasDead,
      });

      let finalPages = validatedPages;

      // If pages are truly empty, check legacy elements field before applying thumbnail fallback
      if (totalElements === 0) {
        const legacyElements = Array.isArray(template.elements) && template.elements.length > 0 ? template.elements : null;
        if (legacyElements) {
          console.log('[Template] Pages empty — merging legacy elements field onto first page', legacyElements.length);
          finalPages = validatedPages.map((page, i) =>
            i === 0 ? { ...page, elements: legacyElements } : page
          );
        } else if (firstBgIsDefault && template.thumbnail) {
          // Last resort: use thumbnail as background so the user sees something
          const thumbOk = await checkImg(template.thumbnail);
          if (thumbOk) {
            const thumbConfig = { type: 'image', url: template.thumbnail, size: 'contain', position: 'center center', opacity: 1, blur: 0 };
            const defaultBgColorsLocal = ['#ffffff', '#fff', 'white', '#f3f4f6'];
            finalPages = validatedPages.map((page) => {
              const hasBg = (page.backgroundConfig?.type === 'image' && page.backgroundConfig?.url) ||
                page.backgroundConfig?.type === 'gradient' ||
                (page.background && !defaultBgColorsLocal.includes(page.background));
              if (!hasBg) {
                return { ...page, background: '#f8f8fc', backgroundConfig: thumbConfig };
              }
              return page;
            });
          }
        }
      }

      const finalFirstPage = finalPages[0];
      const bg = finalFirstPage.background || template.canvas_background || '#ffffff';

      setPages(finalPages);
      setCurrentPageId(finalFirstPage.id);
      if (template.canvas_width && template.canvas_height) {
        setCanvasSize({ width: template.canvas_width, height: template.canvas_height });
      }
      setCanvasBackground(bg);
      setBackgroundConfig(finalFirstPage.backgroundConfig || {
        type: 'color', color: bg, size: 'cover', position: 'center center', opacity: 1, blur: 0,
      });
      setDesignName(template.name);
      setSelectedElement(null);
      setSelectedElements([]);
      toast({ title: 'Template loaded', description: template.name });
      return;
    }

    // ── Path B: legacy flat elements array ────────────────────────────────
    console.warn('[Template] No template_json.pages found — falling back to legacy elements field', {
      legacyElementsCount: template.elements?.length ?? 0,
    });

    const hasElements = Array.isArray(template.elements) && template.elements.length > 0;
    const legacyBg = template.background || template.canvas_background;

    if (!hasElements && !legacyBg) {
      console.error('[Template] No usable design data found in template:', template.id || template.name);
      toast({ title: 'Template has no design data', description: 'This template was not saved with any design content.', variant: 'destructive' });
      return;
    }

    if (hasElements) {
      setPages(prev => prev.map(page =>
        page.id === currentPageId ? { ...page, elements: template.elements } : page
      ));
    }
    if (legacyBg) {
      setCanvasBackground(legacyBg);
      setPages(prev => prev.map(page =>
        page.id === currentPageId ? { ...page, background: legacyBg } : page
      ));
    }
    setDesignName(template.name);
    setSelectedElement(null);
    setSelectedElements([]);
    toast({ title: 'Template loaded', description: `${template.name} applied` });
  };

  // ── Share modal: fetch authoritative share_settings + can_edit ───────────
  // MUST stay above every early return below so the hook count is stable
  // across renders (React #310 otherwise). Backend decides edit permission
  // from JWT role + template ownership; this effect just consumes that.
  const _shareModalTemplateId =
    (isAdminTemplateEdit && adminTemplateId) ||
    (isSharedSession && templateIdFromUrl) ||
    sourceTemplateId ||
    templateIdFromUrl ||
    null;
  useEffect(() => {
    if (!showShareModal || !_shareModalTemplateId) return;
    let cancelled = false;
    const token = getEditorToken();
    if (!token || !API_URL) {
      setShareEditPermission(false);
      return;
    }
    fetch(`${API_URL}/api/templates/${_shareModalTemplateId}/share-info`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        if (data.share_settings) setTemplateShareSettings(data.share_settings);
        setShareEditPermission(Boolean(data.can_edit_share_settings));
        setTemplateShareInfo(data);
      })
      .catch(() => { if (!cancelled) setShareEditPermission(false); });
    return () => { cancelled = true; };
  }, [showShareModal, _shareModalTemplateId]);

  if (designLoading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-gray-100">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 font-medium">Loading design...</p>
        </div>
      </div>
    );
  }

  if (templateLoadError) {
    return (
      <TemplateUnavailable
        title={templateLoadError.title}
        message={templateLoadError.message}
        onBrowse={() => navigate('/templates')}
        onRetry={templateLoadError.canRetry ? () => window.location.reload() : undefined}
      />
    );
  }

  // Safety fallback: pages should never be empty at render time
  if (!pages || pages.length === 0) {
    console.warn('[Canvas] Safety fallback triggered — pages is empty, resetting to blank page');
    return null;
  }
  if (!pages[0].elements || pages[0].elements.length === 0) {
    console.warn('[Canvas] First page has no elements — canvas will render empty');
  }

  return (
    <div className="h-screen flex flex-col">
      {isSharedSession && (
        <SharedSessionBanner
          onUpgrade={() => setShowSharedUpgrade(true)}
          onLearnMore={() => navigate('/pricing')}
        />
      )}
      {showSharedUpgrade && (
        <UpgradeModal
          onClose={() => setShowSharedUpgrade(false)}
          feature="save"
        />
      )}
      {/* Share button → modal. Three contexts produce a templateId:
            - admin editing a template at /admin/templates/{id}/edit
            - free user in a shared session
            - any signed-in user with a design copied from a template
          Everything else falls into the "Publish as Template" view. */}
      {showShareModal && (() => {
        // Resolution priority. Any branch with a real template id wins.
        // The final `templateIdFromUrl` branch catches the case where the
        // user is at /editor?template_id=X without &session=shared — the
        // legacy pre-Phase-D path and any direct/bookmarked URL hit.
        const resolvedTemplateId =
          (isAdminTemplateEdit && adminTemplateId) ||
          (isSharedSession && templateIdFromUrl) ||
          sourceTemplateId ||
          templateIdFromUrl ||
          null;
        // Edit access comes from the backend share-info call (fired in
        // the on-open effect just below). Authoritative — replaces the
        // earlier URL-based heuristic which incorrectly read-only'd
        // designers viewing their own templates via /editor routes.
        const canEditSettings = Boolean(resolvedTemplateId && shareEditPermission);
        const handleSaveShareSettings = async (next) => {
          const token = getEditorToken();
          if (!token || !resolvedTemplateId) return;
          const isAdminToken = Boolean(localStorage.getItem('admin_token'));
          const url = isAdminToken
            ? `${API_URL}/api/admin/content/templates/${resolvedTemplateId}`
            : `${API_URL}/api/designer/templates/${resolvedTemplateId}`;
          // C9 backend honors top-level access_level — forward it so
          // promotion / demotion takes effect without relying solely on
          // the share_settings.is_public dual-write.
          const body = { share_settings: next };
          if (next?.access_level) body.access_level = next.access_level;
          let res;
          try {
            res = await fetch(url, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(body),
            });
          } catch (netErr) {
            toast({
              title: "Couldn't reach the server",
              description: 'Check your connection and try again.',
              variant: 'destructive',
            });
            throw netErr;
          }
          if (!res.ok) {
            // Map common auth statuses to friendly copy + raise a
            // code-bearing Error so the Share modal can surface inline
            // copy AND we can pop a toast (visible even when the modal
            // closes mid-flight).
            let friendly = `HTTP ${res.status}`;
            if (res.status === 401) {
              friendly = 'Your session has expired. Please sign in again.';
            } else if (res.status === 403) {
              friendly = "You don't have permission to change this template's access level.";
            } else if (res.status >= 500) {
              friendly = 'Something went wrong on our end. Try again in a moment.';
            }
            toast({
              title: "Couldn't update access level",
              description: friendly,
              variant: 'destructive',
            });
            const e = new Error(friendly);
            e.status = res.status;
            throw e;
          }
          setTemplateShareSettings(next);
          toast({ title: 'Share settings updated' });
        };
        // Design-share handlers — only meaningful when there's no
        // templateId in play AND the document has been saved (has a
        // designId). The modal decides which mode to render based on
        // whether onEnableDesignShare is non-null.
        const designIdForShare = designIdFromUrl || designId || null;
        const handleEnableDesignShare = async () => {
          const token = getEditorToken();
          if (!token || !designIdForShare) return;
          const res = await fetch(
            `${API_URL}/api/designs/${designIdForShare}/share`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(text || `HTTP ${res.status}`);
          }
          const body = await res.json();
          setDesignShareToken(body.share_token);
          toast({ title: 'Share link enabled' });
        };
        const handleRevokeDesignShare = async () => {
          const token = getEditorToken();
          if (!token || !designIdForShare) return;
          const res = await fetch(
            `${API_URL}/api/designs/${designIdForShare}/share`,
            {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (!res.ok) {
            const text = await res.text().catch(() => '');
            throw new Error(text || `HTTP ${res.status}`);
          }
          setDesignShareToken(null);
          toast({ title: 'Share link revoked' });
        };
        return (
          <ShareDesignModal
            open
            onClose={() => setShowShareModal(false)}
            templateId={resolvedTemplateId}
            templateName={
              resolvedTemplateId
                ? (sharedTemplateName || designName || null)
                : null
            }
            shareSettings={resolvedTemplateId ? templateShareSettings : null}
            templateStats={resolvedTemplateId ? templateShareInfo?.stats : null}
            canEditSettings={canEditSettings}
            onSaveShareSettings={canEditSettings ? handleSaveShareSettings : null}
            designId={designIdForShare}
            designName={designName}
            designShareToken={designShareToken}
            ownerDisplayName={(() => {
              try {
                const raw = localStorage.getItem('ivisora_user') || localStorage.getItem('user');
                const u = raw ? JSON.parse(raw) : null;
                return u?.name || u?.email || null;
              } catch { return null; }
            })()}
            currentUserId={(() => {
              try {
                const raw = localStorage.getItem('ivisora_user') || localStorage.getItem('user');
                return raw ? (JSON.parse(raw)?.id || null) : null;
              } catch { return null; }
            })()}
            ownerIsCurrentUser={(() => {
              // Source of truth: share-info's owner_id for templates,
              // design.user_id for designs. Hardcoding true gave every
              // viewer the Invite + role-management UI, then the
              // backend correctly 403'd. The hardcode was the bug.
              try {
                const raw = localStorage.getItem('ivisora_user') || localStorage.getItem('user');
                const me = raw ? JSON.parse(raw) : null;
                const myId = me?.id || null;
                if (!myId) return false;
                if (resolvedTemplateId) {
                  const tplOwnerId = templateShareInfo?.owner_id;
                  return Boolean(tplOwnerId && tplOwnerId === myId);
                }
                // Design mode — the user opened their own /editor/:id,
                // so they own it. The viewer-share route (/design/:token)
                // is a different page entirely.
                return Boolean(designIdForShare);
              } catch { return false; }
            })()}
            // Design Sharing must work for ANY owned design — including
            // designs derived from a template (the design copy is still
            // shareable separately from the source template).
            onEnableDesignShare={designIdForShare ? handleEnableDesignShare : null}
            onRevokeDesignShare={designIdForShare ? handleRevokeDesignShare : null}
            onOpenPreview={() => {
              if (!resolvedTemplateId) return;
              if (typeof window !== 'undefined') {
                window.open(`/template/${resolvedTemplateId}`, '_blank', 'noopener');
              }
            }}
            onPublishAsTemplate={() => {
              setShowShareModal(false);
              if (isDesignerMode) {
                // Designers have an existing submission flow already.
                setShowDesignerSubmitModal && setShowDesignerSubmitModal(true);
                return;
              }
              toast({
                title: 'Publishing not available',
                description:
                  'Only designers and admins can publish templates today.',
              });
            }}
          />
        );
      })()}
      {/* Top Header */}
      <header className="h-16 bg-white border-b flex items-center justify-between px-4 sm:px-6" data-testid="editor-header">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            aria-label="iVisora — home"
            className="flex items-center cursor-pointer bg-transparent border-0 p-0"
          >
            <IvisoraLogo className="h-6 sm:h-7" />
          </button>
          <Dialog open={showNewDesignDialog} onOpenChange={setShowNewDesignDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="hidden md:flex items-center gap-2" data-testid="new-design-btn">
                <Plus className="w-4 h-4" />
                New Design
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Design</DialogTitle>
                <DialogDescription>
                  Choose custom canvas size for your new design
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="width">Width (px)</Label>
                    <Input
                      id="width"
                      type="number"
                      value={newCanvasWidth}
                      onChange={(e) => setNewCanvasWidth(Number(e.target.value))}
                      min="100"
                      max="5000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="height">Height (px)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={newCanvasHeight}
                      onChange={(e) => setNewCanvasHeight(Number(e.target.value))}
                      min="100"
                      max="5000"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Popular Sizes</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setNewCanvasWidth(1080); setNewCanvasHeight(1080); }}>
                      Square (1080x1080)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setNewCanvasWidth(1920); setNewCanvasHeight(1080); }}>
                      HD (1920x1080)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setNewCanvasWidth(1080); setNewCanvasHeight(1920); }}>
                      Story (1080x1920)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setNewCanvasWidth(1200); setNewCanvasHeight(630); }}>
                      Facebook (1200x630)
                    </Button>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewDesignDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateNewDesign} className="bg-purple-600 hover:bg-purple-700">
                  Create Design
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {isSharedSession && sharedTemplateName && (
            <button
              type="button"
              onClick={() => navigate(`/template/${templateIdFromUrl}`)}
              className="hidden md:flex items-center gap-1.5 text-xs text-gray-600 hover:text-purple-700 px-2 py-1 rounded border border-gray-200 hover:border-purple-300 transition-colors"
              data-testid="template-attribution"
              title="Open template preview"
            >
              <span className="text-gray-500">Created from:</span>
              <span className="font-medium truncate max-w-[200px]">{sharedTemplateName}</span>
            </button>
          )}
          <Button
            variant="outline"
            onClick={isSharedSession
              ? () => setShowSharedUpgrade(true)
              : (isAdminTemplateEdit ? handleAdminTemplateSave : isCreatorMode ? handleCreatorSave : isDesignerMode ? handleDesignerSave : handleSave)
            }
            disabled={!isSharedSession && (saveLoading || designLoading || pendingUploads > 0)}
            size="sm"
            data-testid="save-btn"
            title={isSharedSession
              ? 'Upgrade to Pro to save designs.'
              : (pendingUploads > 0 ? 'Storing images…' : undefined)
            }
            className={isSharedSession ? 'opacity-60 cursor-not-allowed' : ''}
          >
            <Save className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{saveLoading ? 'Saving...' : pendingUploads > 0 ? 'Storing images…' : 'Save'}</span>
          </Button>
          {isDesignerMode && (
            <Button
              size="sm"
              onClick={openDesignerSubmitModal}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5"
              data-testid="designer-submit-btn"
            >
              <Send className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Submit for Approval</span>
            </Button>
          )}
          <Button
            size="sm"
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
            data-testid="share-btn"
            onClick={() => setShowShareModal(true)}
          >
            <Share2 className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </div>
      </header>

      {/* Main Editor */}
      <div className="flex-1 flex overflow-hidden">
        {/* New Two-Panel Left Sidebar */}
        <LeftToolbar
          activePanel={activePanel}
          onPanelChange={setActivePanel}
        />

        {cropElementId && (
          <CropPanel
            element={pages.flatMap((p) => p.elements || []).find((e) => e.id === cropElementId) || null}
            onUpdateElement={updateElement}
            onCommit={handleCommitCrop}
            onCancel={handleCancelCrop}
          />
        )}
        <ContentPanel
          activePanel={cropElementId ? null : activePanel}
          onClose={() => { replaceTargetRef.current = null; setActivePanel(null); }}
          onPickImage={handlePickImage}
          onAddElement={addElement}
          onSetBackground={handleSetBackground}
          backgroundConfig={backgroundConfig}
          onSelectTemplate={handleSelectTemplate}
          categoryFromHome={categoryName}
          brandKit={brandKit}
          onBrandKitChange={handleBrandKitChange}
          onApplyBrand={handleApplyBrand}
          selectedElement={selectedElement}
          onEnterTextEdit={enterTextEdit}
          templateTypeId={effectiveTemplateTypeId}
          onUploadStart={handleUploadStart}
          onUploadEnd={handleUploadEnd}
        />

        {/* Canvas Area with Bottom Panel */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <CanvasEditor
            elements={elements.filter(el => el.visible !== false)}
            selectedElement={selectedElement}
            selectedElements={selectedElements}
            onSelectElement={handleSelectElement}
            onSelectMultiple={handleSelectMultiple}
            onUpdateElement={updateElement}
            onDeleteElement={deleteElement}
            onDeleteSelectedElements={deleteSelectedElements}
            canvasSize={canvasSize}
            onCanvasSizeChange={handleCanvasSizeChange}
            zoom={zoom}
            canvasBackground={canvasBackground}
            backgroundConfig={backgroundConfig}
            projectName={designName}
            onProjectNameChange={setDesignName}
            downloadTemplateId={isSharedSession ? templateIdFromUrl : sourceTemplateId}
            downloadSessionType={isSharedSession ? 'shared_session' : (sourceTemplateId ? 'user_copy' : null)}
            onDownloadQuotaExceeded={() => setShowSharedUpgrade(true)}
            onDownloadDisabled={() => toast({ title: 'Downloads disabled', description: 'The template owner has disabled downloads.', variant: 'destructive' })}
            onAddPage={handleAddPage}
            onDuplicatePage={handleDuplicatePage}
            onLockPage={handleLockPage}
            onDeletePage={handleDeletePage}
            onReorderPages={handleReorderPages}
            onHidePage={handleHidePage}
            pages={pages}
            currentPageId={currentPageId}
            onSwitchPage={handleSwitchPage}
            activeTool={activeTool}
            onToolChange={handleToolChange}
            onAddElement={addElement}
            brushSettings={brushSettings}
            setBrushSettings={setBrushSettings}
            selectedBrushPreset={selectedBrushPreset}
            setSelectedBrushPreset={setSelectedBrushPreset}
            eraserSettings={eraserSettings}
            setEraserSettings={setEraserSettings}
            isAnimationPlaying={isAnimationPlaying}
            animationPlayKey={animationPlayKey}
            editRequest={editRequest}
            onUndo={handleUndo}
            onRedo={handleRedo}
            canUndo={canUndo}
            canRedo={canRedo}
            onInteractionStart={saveToHistory}
            onInteractionChange={setIsInteracting}
            editingGroupId={editingGroupId}
            onSetEditingGroupId={setEditingGroupId}
            previewFilter={previewFilter}
            cropElementId={cropElementId}
            onStartCrop={handleStartCrop}
            onCommitCrop={handleCommitCrop}
            onCancelCrop={handleCancelCrop}
            onUploadImageFromUrl={uploadImageFromUrl}
          />

          {/* Floating text formatting toolbar. Anchored to the selected text
              element via fixed positioning; tracks drag/resize/scroll/zoom.
              Hides during drag/resize/rotate via `isInteracting` so it never
              overlaps a transform interaction. When multiple text elements
              are selected, anchors to their union bbox so the toolbar sits
              above the entire group instead of over the primary element. */}
          {selectedElement?.type === 'text' && (() => {
            let boundingBox = null;
            if (
              selectedElements
              && selectedElements.length >= 2
              && selectedElements.every((el) => el.type === 'text')
            ) {
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              for (const el of selectedElements) {
                const x = Number(el.x) || 0;
                const y = Number(el.y) || 0;
                const w = Number(el.width) || 0;
                const h = Number(el.height) || 0;
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x + w > maxX) maxX = x + w;
                if (y + h > maxY) maxY = y + h;
              }
              if (Number.isFinite(minX) && maxX > minX && maxY > minY) {
                boundingBox = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
              }
            }
            return (
              <TextFloatingToolbar
                element={selectedElement}
                boundingBox={boundingBox}
                pageCanvasId={`page-canvas-${currentPageId}`}
                zoom={zoom}
                onUpdateElement={updateElement}
                isInteracting={isInteracting}
                onDuplicate={() => duplicateElement(selectedElement)}
                onDelete={() => deleteElement(selectedElement.id)}
                onToggleLock={() => toggleElementLock(selectedElement.id)}
                onLayerOrder={(dir) => handleMoveLayer(selectedElement.id, dir)}
              />
            );
          })()}

          {/* Floating toolbar for image/frame elements (replace, crop, flip,
              fit, rotation). Hidden during crop mode — crop has its own UI. */}
          {selectedElement && ['image', 'frame', 'svg', 'shape', 'sticker', 'penPath', 'drawing'].includes(selectedElement.type) && !cropElementId && (
            <ImageFloatingToolbar
              element={selectedElement}
              pageCanvasId={`page-canvas-${currentPageId}`}
              zoom={zoom}
              onUpdateElement={updateElementWithHistory}
              isInteracting={isInteracting}
              onRequestReplace={handleRequestReplace}
              onRequestCrop={() => handleStartCrop(selectedElement.id)}
              onDuplicate={() => duplicateElement(selectedElement)}
              onDelete={() => deleteElement(selectedElement.id)}
              onToggleLock={() => toggleElementLock(selectedElement.id)}
              onLayerOrder={(dir) => handleMoveLayer(selectedElement.id, dir)}
              onConvertToFrame={() => convertToFrame(selectedElement.id)}
              onSaveAsFrame={() => saveAsCustomFrame(selectedElement.id)}
            />
          )}

          {/* Slide-up pages panel */}
          <PagesBottomPanel
            show={showPagePanel}
            pages={pages}
            currentPageId={currentPageId}
            canvasSize={canvasSize}
            onPageChange={handleSwitchPage}
            onAddPage={handleAddPage}
            onAddPageAfter={handleAddPageAfter}
            onDeletePage={handleDeletePage}
            onDuplicatePage={handleDuplicatePageById}
            onRenamePage={handleRenamePage}
            onLockPage={handleLockPage}
            onReorderPages={handleReorderPages}
          />

          {/* Always-visible bottom toolbar */}
          <CanvasBottomBar
            zoom={zoom}
            setZoom={setZoom}
            onFitToScreen={handleFitToScreen}
            pages={pages}
            currentPageId={currentPageId}
            showPagesPanel={showPagePanel}
            onTogglePagesPanel={() => setShowPagePanel(prev => !prev)}
            onToggleGridView={() => setShowGridView(prev => !prev)}
          />

          {/* Grid view modal */}
          {showGridView && (
            <GridViewModal
              pages={pages}
              currentPageId={currentPageId}
              canvasSize={canvasSize}
              onSwitchPage={(id) => { handleSwitchPage(id); setShowGridView(false); }}
              onClose={() => setShowGridView(false)}
            />
          )}
        </div>

        <RightSidebar
          selectedElement={selectedElement}
          selectedElements={selectedElements}
          onUpdateElement={updateElement}
          onUpdateElementWithHistory={updateElementWithHistory}
          onDeleteElement={deleteElement}
          onDeleteSelectedElements={deleteSelectedElements}
          onDuplicateElement={duplicateElement}
          onDuplicateSelectedElements={duplicateSelectedElements}
          elements={elements}
          onSelectElement={handleSelectElement}
          onMoveLayer={handleMoveLayer}
          onReorderLayer={handleReorderLayer}
          onApplyMask={handleApplyMask}
          onRemoveMask={handleRemoveMask}
          onReplaceImage={replaceImageSrc}
          onPlayAnimation={handlePlayAnimation}
          isAnimationPlaying={isAnimationPlaying}
          brandKit={brandKit}
          activeTool={activeTool}
          brushSettings={brushSettings}
          setBrushSettings={setBrushSettings}
          selectedBrushPreset={selectedBrushPreset}
          setSelectedBrushPreset={setSelectedBrushPreset}
          eraserSettings={eraserSettings}
          setEraserSettings={setEraserSettings}
          onPreviewFilter={setPreviewFilter}
          onAlignSelected={alignSelectedElements}
          onDistributeSelected={distributeSelectedElements}
        />
      </div>

      {/* ── Designer Submit for Approval Modal (uses Dialog for correct portal z-index) ── */}
      <Dialog open={showDesignerSubmitModal} onOpenChange={open => { if (!open) setShowDesignerSubmitModal(false); }}>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden flex flex-col max-h-[92vh]">

          {/* Header */}
          <DialogHeader className="flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Send className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-gray-900">Submit for Approval</DialogTitle>
                <DialogDescription className="text-xs text-gray-400 mt-0">
                  Fill in the details before sending to admin review
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

            {/* Canvas info strip */}
            <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 flex items-center gap-4">
              <div className="w-12 h-10 bg-white rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] text-gray-400 tabular-nums leading-tight text-center">
                  {canvasSize.width}<br />×{canvasSize.height}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700 truncate">{designName}</p>
                <p className="text-xs text-gray-400">{pages.length} page{pages.length !== 1 ? 's' : ''} · {canvasSize.width}×{canvasSize.height}px</p>
              </div>
              <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full flex-shrink-0">
                Will be Pending review
              </span>
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">
                Template Title <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="e.g. Modern Social Media Sale Template"
                value={designerSubmitForm.title}
                onChange={e => setDesignerSubmitForm(f => ({ ...f, title: e.target.value }))}
                className="border-gray-200"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">Description</Label>
              <Textarea
                placeholder="What is this template for? Who is the target audience?"
                value={designerSubmitForm.description}
                onChange={e => setDesignerSubmitForm(f => ({ ...f, description: e.target.value }))}
                className="border-gray-200 resize-none h-20 text-sm"
              />
            </div>

            {/* Template Type */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">Template Type</Label>
              <Select
                value={designerSubmitForm.template_type_id}
                onValueChange={typeId => {
                  setDesignerSubmitForm(f => ({ ...f, template_type_id: typeId, category: '' }));
                }}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder={designerTemplateTypes.length === 0 ? 'Loading…' : 'Select type'} />
                </SelectTrigger>
                <SelectContent>
                  {designerTemplateTypes.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.icon ? `${t.icon} ` : ''}{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category (dependent on type) */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700">Category</Label>
              <Select
                value={designerSubmitForm.category}
                onValueChange={v => setDesignerSubmitForm(f => ({ ...f, category: v }))}
                disabled={!designerSubmitForm.template_type_id}
              >
                <SelectTrigger className="border-gray-200">
                  <SelectValue placeholder={
                    !designerSubmitForm.template_type_id
                      ? 'Select a type first'
                      : designerCategories.length === 0
                        ? 'No categories for this type'
                        : 'Select category (optional)'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {designerCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tags */}
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5" /> Tags
              </Label>
              <Input
                placeholder="e.g. sale, marketing, instagram (comma-separated)"
                value={designerSubmitForm.tags}
                onChange={e => setDesignerSubmitForm(f => ({ ...f, tags: e.target.value }))}
                className="border-gray-200"
              />
            </div>

            {/* Preview image */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">Design Preview</Label>
                {(designerThumbnail || autoCapturing) && (
                  <span className="text-[11px] font-medium flex items-center gap-1">
                    {autoCapturing
                      ? <span className="text-amber-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Generating{pages.length > 1 ? ` ${designerPagePreviews.filter(Boolean).length}/${pages.length} pages` : ''}…</span>
                      : <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3 h-3" />Auto-captured</span>
                    }
                  </span>
                )}
              </div>

              {/* Main preview area */}
              {autoCapturing && !designerPagePreviews[previewPageIdx] ? (
                <div className="h-48 rounded-xl bg-gray-50 border border-gray-200 flex flex-col items-center justify-center gap-2">
                  <Loader2 className="w-6 h-6 text-violet-500 animate-spin" />
                  <p className="text-xs text-gray-400">Capturing canvas preview…</p>
                </div>
              ) : (pages[previewPageIdx] || pages[0]) ? (
                <div
                  className="mx-auto relative rounded-xl overflow-hidden border border-gray-200 bg-[#f8f9fb]"
                  style={{
                    height: 280,
                    width: Math.round(280 * canvasSize.width / canvasSize.height),
                    maxWidth: '100%',
                  }}
                >
                  {/* Always use React-based preview — accurately renders SVG clip-paths and gradients.
                      html2canvas output is captured in the background for the admin submission URL only. */}
                  <PageThumbnailPreview
                    page={pages[previewPageIdx] || pages[0]}
                    canvasSize={canvasSize}
                    fillContainer={true}
                  />
                  {pages.length > 1 && (
                    <div className="absolute bottom-2 left-2 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded-full font-medium z-10">
                      Page {previewPageIdx + 1} of {pages.length}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                  <Loader2 className="w-5 h-5 text-gray-300 mb-1" />
                  <span className="text-xs text-gray-400">Preview will be auto-captured from your canvas</span>
                </div>
              )}

              {/* Page filmstrip — multi-page only */}
              {pages.length > 1 && (designerPagePreviews.some(Boolean) || autoCapturing) && (
                <div className="flex gap-2 overflow-x-auto pb-1 pt-0.5">
                  {pages.map((page, i) => {
                    const url = designerPagePreviews[i];
                    const isActive = previewPageIdx === i;
                    return (
                      <button
                        key={page.id}
                        onClick={() => { setPreviewPageIdx(i); if (url) { setDesignerThumbnail(url); setDesignerPreview(url); } }}
                        className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all w-14 h-14 flex items-center justify-center ${
                          isActive ? 'border-violet-500 ring-2 ring-violet-200' : 'border-gray-200 hover:border-gray-400'
                        }`}
                        title={`Page ${i + 1}`}
                      >
                        {url
                          ? <img src={url} alt={`P${i + 1}`} className="w-full h-full object-cover" />
                          : <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              {autoCapturing
                                ? <Loader2 className="w-3 h-3 text-violet-400 animate-spin" />
                                : <span className="text-[10px] text-gray-400">P{i + 1}</span>
                              }
                            </div>
                        }
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Info note */}
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
              Your canvas data is automatically included. An admin will review within 24–48 hours.
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <Button variant="outline" onClick={() => setShowDesignerSubmitModal(false)} disabled={designerSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleDesignerSubmit}
              disabled={designerSubmitting || !designerSubmitForm.title.trim()}
              className="bg-violet-600 hover:bg-violet-700 gap-2"
            >
              {designerSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {designerSubmitting ? 'Submitting…' : 'Submit Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const EditorPageWithBoundary = () => (
  <EditorErrorBoundary>
    <EditorPage />
  </EditorErrorBoundary>
);

export default EditorPageWithBoundary;
